import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  Avatar,
  Badge,
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  List,
  Modal,
  Pagination,
  DatePicker,
  Row,
  Select,
  Space,
  Spin,
  Statistic,
  Divider,
  Tabs,
  Tag,
  Typography,
  Upload,
  message
} from 'antd'
import {
  CarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  LogoutOutlined,
  SearchOutlined,
  UserOutlined
} from '@ant-design/icons'
import dayjs from 'dayjs'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import { useVehicleStore } from '../stores/vehicleStore'
import { simpleDirectCloudApi } from '../services/directCloudApi.simple'
import { app, ensureLogin } from '../services/tcbClient'
import type { Vehicle, VehicleAdjustment } from '../types'
import {
  allocatePaymentByPeriod as allocatePaymentByPeriodUtil,
  formatPeriodLabel,
  getActiveLeaseStart,
  getExpectedBetween,
  getVehicleDueDate,
  hasExplicitChargeRange,
  isReceivableVehicleStatus,
  isLeaseStarted,
  getPeriodDeficits as getPeriodDeficitsUtil,
  type PreviewItem,
  type ReceiptRecord
} from './vehicleManagerUtils'
import { ExpiringVehiclesModal, PreviewModal, RentModal } from './VehicleManagerModals'

const { Title, Text } = Typography
const { Option } = Select
const rentPeriodOptions = [
  { value: 'monthly', label: '月付' },
  { value: 'quarterly', label: '季付' },
  { value: 'halfyear', label: '半年付' },
  { value: 'yearly', label: '年付' },
  { value: 'custom', label: '自定义' }
]
const reminderPeriodOptions = [
  { value: 'monthly', label: '月付' },
  { value: 'quarterly', label: '季付' },
  { value: 'halfyear', label: '半年付' },
  { value: 'yearly', label: '年付' },
  { value: 'custom', label: '自定义' }
]
const receivableModeOptions = [
  { value: 'daily_365', label: '按365日均摊' },
  { value: 'monthly_plan', label: '按年金/收款月数' }
]

const VehicleManagerPage: React.FC = () => {
  const navigate = useNavigate()
  const { user, logout, checkPermission } = useAuthStore()
  const { vehicles, statistics, fetchVehicles, fetchStatistics } = useVehicleStore()

  const [activeTab, setActiveTab] = useState<'rent' | 'returnedDebt' | 'pending' | 'paid' | 'rentAdjust'>('rent')
  const [rentModalVisible, setRentModalVisible] = useState(false)
  const [expiringModalVisible, setExpiringModalVisible] = useState(false)
  const [loading, setLoading] = useState(true)
  const [searchKeyword, setSearchKeyword] = useState('')
  const [signedDocSearchKeyword, setSignedDocSearchKeyword] = useState('')
  const [areaFilter, setAreaFilter] = useState<'all' | 'A' | 'B' | 'C' | 'D'>('all')
  const [showAllExpiring, setShowAllExpiring] = useState(false)
  const [expiringPage, setExpiringPage] = useState(1)
  const [expiringPageSize, setExpiringPageSize] = useState(8)
  const [activeExpiringVehicleId, setActiveExpiringVehicleId] = useState<string>('')
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [fileList, setFileList] = useState<any[]>([])
  const [previewVisible, setPreviewVisible] = useState(false)
  const [submittingReceipt, setSubmittingReceipt] = useState(false)
  const [previewItems, setPreviewItems] = useState<PreviewItem[]>([])
  const [pendingValues, setPendingValues] = useState<any>(null)
  const [tailCollectionRow, setTailCollectionRow] = useState<any>(null)
  const [pendingReceipts, setPendingReceipts] = useState<ReceiptRecord[]>([])
  const [paidReceipts, setPaidReceipts] = useState<ReceiptRecord[]>([])
  const [allReceipts, setAllReceipts] = useState<ReceiptRecord[]>([])
  const [adjustments, setAdjustments] = useState<VehicleAdjustment[]>([])
  const [leaseModalVisible, setLeaseModalVisible] = useState(false)
  const [leaseForm] = Form.useForm()
  const leaseType = Form.useWatch('type', leaseForm)
  const leaseUnitName = Form.useWatch('unitName', leaseForm)
  const leaseVehicleId = Form.useWatch('vehicleId', leaseForm)
  const leaseMakerId = Form.useWatch('makerId', leaseForm)
  const leaseRentPeriod = Form.useWatch('rentPeriod', leaseForm)
  const leaseReceivableMode = Form.useWatch('receivableMode', leaseForm)
  const [rentAdjustModalVisible, setRentAdjustModalVisible] = useState(false)
  const [rentAdjustForm] = Form.useForm()
  const rentAdjustVehicleId = Form.useWatch('vehicleId', rentAdjustForm)
  const rentAdjustMakerId = Form.useWatch('makerId', rentAdjustForm)
  const [rentAdjustFileList, setRentAdjustFileList] = useState<any[]>([])
  const [rentAdjustRequests, setRentAdjustRequests] = useState<any[]>([])
  const [signedLeaseForms, setSignedLeaseForms] = useState<any[]>([])
  const [leaseMakerSealPreviewUrl, setLeaseMakerSealPreviewUrl] = useState('')
  const [rentAdjustMakerSealPreviewUrl, setRentAdjustMakerSealPreviewUrl] = useState('')
  const [selectedVehicles, setSelectedVehicles] = useState<Vehicle[]>([])
  const [mergeByCompany, setMergeByCompany] = useState(false)
  const [form] = Form.useForm()
  const [makers, setMakers] = useState<any[]>([])
  const [customerLedgerVisible, setCustomerLedgerVisible] = useState(false)
  const [dailyReportVisible, setDailyReportVisible] = useState(false)
  const [customerLedgerForm] = Form.useForm()
  const [dailyReportForm] = Form.useForm()
  const rentDetailsRef = useRef<HTMLDivElement | null>(null)
  const canManageVehicles = checkPermission('manage_vehicles')
  const canViewBoss = checkPermission('view_boss')
  useEffect(() => {
    loadData()
  }, [])

  useEffect(() => {
    if (!leaseModalVisible) return
    const currentMakerId = leaseForm.getFieldValue('makerId')
    if (currentMakerId) return
    const match = makers.find(m =>
      [m._id, m.id, m.username, m.nickName].some(val => String(val || '') === String(user?.id || user?.nickName || ''))
    )
    if (match) {
      leaseForm.setFieldsValue({ makerId: String(match._id || match.id || match.username || match.nickName) })
    }
  }, [leaseModalVisible, leaseForm, user, makers])

  useEffect(() => {
    if (!rentAdjustModalVisible) return
    const currentMakerId = rentAdjustForm.getFieldValue('makerId')
    if (currentMakerId) return
    const match = makers.find(m =>
      [m._id, m.id, m.username, m.nickName].some(val => String(val || '') === String(user?.id || user?.nickName || ''))
    )
    if (match) {
      rentAdjustForm.setFieldsValue({ makerId: String(match._id || match.id || match.username || match.nickName) })
    }
  }, [rentAdjustModalVisible, rentAdjustForm, user, makers])

  useEffect(() => {
    const vehicle = vehicles.find(v => String(v._id || v.id || v.plate) === String(leaseVehicleId))
    if (!vehicle) return
    if (!leaseType || leaseType === 'return') {
      const currentUnitName = String(leaseForm.getFieldValue('unitName') || '')
      if (currentUnitName !== String(vehicle.company || '')) {
        leaseForm.setFieldsValue({ unitName: vehicle.company || '' })
      }
      return
    }
    leaseForm.setFieldsValue({
      rentAmount: vehicle.rentAmount,
      rentPeriod: vehicle.rentPeriod || 'yearly',
      rentPeriodMonths: vehicle.rentPeriodMonths,
      reminderPeriod: vehicle.reminderPeriod || vehicle.rentPeriod || 'monthly',
      reminderPeriodMonths: vehicle.reminderPeriodMonths,
      reminderAmount: vehicle.reminderAmount ?? vehicle.rentAmount,
      receivableMode: vehicle.receivableMode || 'daily_365',
      receivableMonths: vehicle.receivableMonths,
      address: vehicle.address,
      notes: leaseForm.getFieldValue('notes') || ''
    })
  }, [leaseType, leaseVehicleId, vehicles, leaseForm])

  const openLeaseModal = () => {
    leaseForm.resetFields()
    const match = makers.find(m =>
      [m._id, m.id, m.username, m.nickName].some(val => String(val || '') === String(user?.id || user?.nickName || ''))
    )
    const defaultMakerId = match ? String(match._id || match.id || match.username || match.nickName) : undefined
    leaseForm.setFieldsValue({
      type: 'return',
      makerId: defaultMakerId,
      returnTime: dayjs()
    })
    setLeaseModalVisible(true)
  }

  const loadData = async () => {
    try {
      setLoading(true)
      await Promise.all([fetchVehicles(), fetchStatistics()])
      const [pendingResult, paidResult, allResult, adjustmentsResult, makersResult, rentAdjustResult] = await Promise.all([
        simpleDirectCloudApi.getReceipts('submitted'),
        simpleDirectCloudApi.getReceipts('accounted'),
        simpleDirectCloudApi.getReceipts(),
        simpleDirectCloudApi.getVehicleAdjustments(),
        simpleDirectCloudApi.getAvailableUsers(),
        simpleDirectCloudApi.getRentAdjustRequests()
      ])
      const leaseResult = await simpleDirectCloudApi.getLeaseForms('signed')
      setPendingReceipts(pendingResult.success && pendingResult.data ? pendingResult.data : [])
      setPaidReceipts(paidResult.success && paidResult.data ? paidResult.data : [])
      setAllReceipts(allResult.success && allResult.data ? allResult.data : [])
      const adjustmentsRaw = adjustmentsResult.success && adjustmentsResult.data ? adjustmentsResult.data : []
      setAdjustments(adjustmentsRaw.filter((adj: any) => adj.type === 'parts' || adj.type === 'repair'))
      const makersRaw = makersResult.success && makersResult.data ? makersResult.data : []
      setMakers(makersRaw.filter((m: any) => m.isActive !== false && m.isActive !== 0))
      setRentAdjustRequests(rentAdjustResult.success && rentAdjustResult.data ? rentAdjustResult.data : [])
      setSignedLeaseForms(leaseResult.success && leaseResult.data ? leaseResult.data : [])
    } catch (err) {
      console.error(err)
      message.error('加载数据失败')
    } finally {
      setLoading(false)
    }
  }
  const normalizeArea = (value?: string) => {
    const raw = String(value || '').trim()
    if (!raw) return ''
    const match = raw.match(/^[A-D]/i)
    return match ? match[0].toUpperCase() : raw.toUpperCase()
  }

  const paidStatusSet = useMemo(() => new Set(['cashier_done', 'accounted', 'completed']), [])
  const getReceiptItems = (receipt: any) => {
    if (Array.isArray(receipt?.items)) return receipt.items
    if (receipt?.items && typeof receipt.items === 'object') return Object.values(receipt.items)
    return []
  }
  const getReceiptEventTime = (receipt: any) =>
    dayjs(receipt?.verifiedAt || receipt?.accountedAt || receipt?.updatedAt || receipt?.createdAt)
  const getLeaseReturnTime = (doc: any) =>
    dayjs(doc?.returnTime || doc?.signedAt || doc?.updatedAt || doc?.createdAt)
  const getLeaseHistoryAtDate = (vehicle: Vehicle, at: dayjs.Dayjs) => {
    const history = Array.isArray((vehicle as any).leaseHistory) ? (vehicle as any).leaseHistory : []
    const records = history
      .filter((item: any) => item?.startDate)
      .sort((a: any, b: any) => dayjs(a.startDate).valueOf() - dayjs(b.startDate).valueOf())
    const active = records.find((item: any) => {
      const start = dayjs(item.startDate)
      const end = item.endDate ? dayjs(item.endDate) : null
      if (!start.isValid() || start.isAfter(at, 'day')) return false
      if (!end || !end.isValid()) return true
      return !end.isBefore(at, 'day')
    })
    if (active) return active
    const latestBefore = [...records]
      .reverse()
      .find((item: any) => {
        const start = dayjs(item.startDate)
        return start.isValid() && !start.isAfter(at, 'day')
      })
    return latestBefore || null
  }
  const hasSubsequentLeaseAfter = (vehicle: Vehicle, at: dayjs.Dayjs) => {
    const history = Array.isArray((vehicle as any).leaseHistory) ? (vehicle as any).leaseHistory : []
    return history.some((item: any) => {
      const start = dayjs(String(item?.startDate || '').trim())
      return start.isValid() && start.isAfter(at, 'day')
    })
  }
  const getHistoricalVehicleSnapshot = (vehicle: Vehicle, at: dayjs.Dayjs) => {
    const lease = getLeaseHistoryAtDate(vehicle, at)
    if (!lease) return { snapshot: vehicle, lease: null }
    return {
      snapshot: {
        ...vehicle,
        currentLeaseId: lease.leaseId,
        startDate: lease.startDate || vehicle.startDate,
        company: lease.company || vehicle.company,
        contactPerson: lease.contactPerson || vehicle.contactPerson,
        contactPerson2: lease.contactPerson2 || (vehicle as any).contactPerson2,
        phone: lease.phone || vehicle.phone,
        phone2: lease.phone2 || (vehicle as any).phone2,
        rentAmount: lease.rentAmount ?? vehicle.rentAmount,
        rentPeriod: lease.rentPeriod || vehicle.rentPeriod,
        rentPeriodMonths: lease.rentPeriodMonths ?? (vehicle as any).rentPeriodMonths
      } as Vehicle,
      lease
    }
  }
  const buildLeaseFormVehicleSnapshot = (vehicle: Vehicle) => ({
    status: vehicle.status,
    company: vehicle.company,
    contactPerson: vehicle.contactPerson,
    contactPerson2: (vehicle as any).contactPerson2,
    phone: vehicle.phone,
    phone2: (vehicle as any).phone2,
    startDate: vehicle.startDate,
    dueDate: vehicle.dueDate,
    reminderDate: (vehicle as any).reminderDate,
    reminderPeriod: vehicle.reminderPeriod,
    reminderPeriodMonths: vehicle.reminderPeriodMonths,
    reminderAmount: vehicle.reminderAmount,
    depositAmount: vehicle.depositAmount,
    rentAmount: vehicle.rentAmount,
    rentPeriod: vehicle.rentPeriod,
    rentPeriodMonths: vehicle.rentPeriodMonths,
    receivableMode: vehicle.receivableMode,
    receivableMonths: vehicle.receivableMonths,
    currentLeaseId: (vehicle as any).currentLeaseId,
    leaseHistory: Array.isArray((vehicle as any).leaseHistory) ? (vehicle as any).leaseHistory : [],
    pricingHistory: Array.isArray((vehicle as any).pricingHistory) ? (vehicle as any).pricingHistory : [],
    address: vehicle.address,
    configuration: vehicle.configuration,
    notes: vehicle.notes,
    area: vehicle.area,
    type: vehicle.type,
    returnSigned: Boolean((vehicle as any).returnSigned),
    contractFiles: Array.isArray((vehicle as any).contractFiles) ? (vehicle as any).contractFiles : []
  })
  const getFrozenOutstandingAtDate = (vehicle: Vehicle, cutoffDate: dayjs.Dayjs) => {
    if (!cutoffDate.isValid()) return getOutstanding(vehicle)
    // Use frontend calculation with the fixed getAnnualReceivableAmount
    const leaseStart = dayjs(getActiveLeaseStart(vehicle))
    const start = leaseStart.isValid() ? leaseStart.format('YYYY-MM-DD') : String(vehicle.startDate || '')
    const end = cutoffDate.format('YYYY-MM-DD')
    const expected = getExpectedBetween(vehicle, start, end)
    let paid = 0
    for (const receipt of allReceipts as any[]) {
      const receiptItems = getReceiptItems(receipt)
      const items = receiptItems.length ? receiptItems : [receipt]
      const receiptTime = getReceiptEventTime(receipt)
      for (const it of items) {
        const itVehicleId = String(it?.vehicleId || receipt?.vehicleId || '').trim()
        const itPlate = String(it?.plate || receipt?.plate || '').trim()
        const matchVehicle =
          itVehicleId === String(vehicle._id || vehicle.id || '').trim() ||
          itPlate === String(vehicle.plate || '').trim()
        if (!matchVehicle) continue
        const isPaid = paidStatusSet.has(String(it?.status || receipt?.status || ''))
        if (!isPaid) continue
        if (receiptTime.isValid() && receiptTime.isAfter(cutoffDate, 'day')) continue
        paid += Number(it?.receivedAmount ?? it?.verifiedAmount ?? it?.amount ?? 0) || 0
      }
    }
    return Math.max(expected - paid, 0)
  }
  const getTailCollectionPaidAfterReturn = (vehicle: Vehicle, returnAt: dayjs.Dayjs) => {
    let paid = 0
    for (const receipt of allReceipts as any[]) {
      const receiptTime = getReceiptEventTime(receipt)
      if (!receiptTime.isValid() || receiptTime.isBefore(returnAt, 'day')) continue
      if (!paidStatusSet.has(String(receipt?.status || '').trim())) continue
      if (!receipt?.tailCollection) continue
      const receiptItems = getReceiptItems(receipt)
      const items = receiptItems.length ? receiptItems : [receipt]
      for (const it of items) {
        const itVehicleId = String(it?.vehicleId || receipt?.vehicleId || '').trim()
        const itPlate = String(it?.plate || receipt?.plate || '').trim()
        const matchVehicle =
          itVehicleId === String(vehicle._id || vehicle.id || '').trim() ||
          itPlate === String(vehicle.plate || '').trim()
        if (!matchVehicle) continue
        paid += Number(it?.actualAmount ?? it?.receivedAmount ?? it?.verifiedAmount ?? it?.amount ?? 0) || 0
      }
    }
    return paid
  }
  const latestSignedReturnByVehicle = useMemo(() => {
    const map = new Map<string, any>()
    const returnForms = (signedLeaseForms || []).filter((doc: any) => String(doc?.type || '') === 'return')
    for (const doc of returnForms) {
      const keys = [doc?.vehicleId, doc?.plate].map(v => String(v || '').trim()).filter(Boolean)
      const t = getLeaseReturnTime(doc)
      for (const key of keys) {
        const prev = map.get(key)
        const prevT = getLeaseReturnTime(prev)
        if (!prev || (t.isValid() && (!prevT.isValid() || t.isAfter(prevT)))) {
          map.set(key, doc)
        }
      }
    }
    return map
  }, [signedLeaseForms])
  const isVehicleEffectivelyRented = (vehicle: Vehicle) => {
    const latestReturn =
      latestSignedReturnByVehicle.get(String(vehicle._id || vehicle.id || '').trim()) ||
      latestSignedReturnByVehicle.get(String(vehicle.plate || '').trim())
    if (!latestReturn) return isReceivableVehicleStatus(vehicle?.status)
    const returnAt = getLeaseReturnTime(latestReturn)
    if (!returnAt.isValid()) return isReceivableVehicleStatus(vehicle?.status)
    return hasSubsequentLeaseAfter(vehicle, returnAt)
      ? true
      : isReceivableVehicleStatus(vehicle?.status) && Boolean((vehicle as any).currentLeaseId)
  }
  const filteredRentedVehicles = useMemo(() => {
    const keyword = searchKeyword.trim().toLowerCase()
    return vehicles
      .filter(v => isVehicleEffectivelyRented(v))
      .filter(v => (areaFilter === 'all' ? true : normalizeArea(v.area) === areaFilter))
      .filter(v => {
        if (!keyword) return true
        const plate = String(v.plate || '').toLowerCase()
        const company = String(v.company || '').toLowerCase()
        const contact1 = String(v.contactPerson || '').toLowerCase()
        const contact2 = String((v as any).contactPerson2 || '').toLowerCase()
        const phone = String(v.phone || '')
        const phone2 = String((v as any).phone2 || '')
        return (
          plate.includes(keyword) ||
          company.includes(keyword) ||
          contact1.includes(keyword) ||
          contact2.includes(keyword) ||
          phone.includes(keyword) ||
          phone2.includes(keyword)
        )
      })
  }, [vehicles, searchKeyword, areaFilter, latestSignedReturnByVehicle])

  const paginatedRentedVehicles = useMemo(() => {
    const start = (currentPage - 1) * pageSize
    return filteredRentedVehicles.slice(start, start + pageSize)
  }, [filteredRentedVehicles, currentPage, pageSize])

  const returnedDebtRows = useMemo(() => {
    const getLastReceiptCutoffBeforeReturn = (vehicle: Vehicle, returnAt: dayjs.Dayjs, fallbackStart?: string) => {
      let latest = ''
      for (const receipt of allReceipts as any[]) {
        const receiptTime = getReceiptEventTime(receipt)
        if (!receiptTime.isValid() || receiptTime.isAfter(returnAt, 'day')) continue
        const receiptItems = getReceiptItems(receipt)
        const items = receiptItems.length ? receiptItems : [receipt]
        for (const it of items) {
          const itVehicleId = String(it?.vehicleId || receipt?.vehicleId || '').trim()
          const itPlate = String(it?.plate || receipt?.plate || '').trim()
          const matchVehicle =
            itVehicleId === String(vehicle._id || vehicle.id || '').trim() ||
            itPlate === String(vehicle.plate || '').trim()
          if (!matchVehicle) continue
          if (!hasExplicitChargeRange(it, receipt)) continue
          const cutoff = String(it?.chargeRangeEnd || receipt?.chargeRangeEnd || '').trim()
          if (!cutoff || !dayjs(cutoff).isValid()) continue
          if (!latest || dayjs(cutoff).isAfter(dayjs(latest), 'day')) {
            latest = cutoff
          }
        }
      }
      return latest || String(fallbackStart || getActiveLeaseStart(vehicle) || vehicle.startDate || '')
    }

    const returnForms = (signedLeaseForms || []).filter((doc: any) => String(doc?.type || '') === 'return')

    return returnForms
      .map((returnDoc: any) => {
        const vehicle = vehicles.find((v: any) =>
          String(v?._id || v?.id || '').trim() === String(returnDoc?.vehicleId || '').trim() ||
          String(v?.plate || '').trim() === String(returnDoc?.plate || '').trim()
        )
        if (!vehicle) return null
        const returnAt = getLeaseReturnTime(returnDoc)
        const { snapshot, lease } = getHistoricalVehicleSnapshot(vehicle, returnAt)
        const frozenOutstanding = getFrozenOutstandingAtDate(snapshot as Vehicle, returnAt)
        const tailCollectedAmount = getTailCollectionPaidAfterReturn(vehicle, returnAt)
        const outstandingAfterTail = Math.max(frozenOutstanding - tailCollectedAmount, 0)
        const periodStart = getLastReceiptCutoffBeforeReturn(snapshot as Vehicle, returnAt, lease?.startDate || snapshot.startDate)
        const periodLabel =
          dayjs(periodStart).isValid() && returnAt.isValid()
            ? `${dayjs(periodStart).format('YYYY-MM-DD')} ~ ${returnAt.format('YYYY-MM-DD')}`
            : '-'
        return {
          vehicle,
          historicalLease: lease,
          returnDoc,
          returnAt,
          frozenOutstanding: outstandingAfterTail,
          frozenOutstandingRaw: frozenOutstanding,
          tailCollectedAmount,
          debtPeriodLabel: periodLabel
        }
      })
      .filter((row: any) => row && row.frozenOutstanding > 0)
      .sort((a: any, b: any) => b.returnAt.valueOf() - a.returnAt.valueOf())
  }, [vehicles, signedLeaseForms, allReceipts, adjustments, paidStatusSet])
  const returnedDebtMap = useMemo(() => {
    const map = new Map<string, any>()
    for (const row of returnedDebtRows) {
      const vehicle = row?.vehicle
      if (!vehicle) continue
      const keys = [vehicle._id, vehicle.id, vehicle.plate]
        .map(v => String(v || '').trim())
        .filter(Boolean)
      for (const key of keys) map.set(key, row)
    }
    return map
  }, [returnedDebtRows])
  const handleLogout = () => {
    Modal.confirm({
      title: '确认退出？',
      onOk: () => {
        logout()
        navigate('/login')
      }
    })
  }

  const handleSearch = (value: string) => {
    setSearchKeyword(value)
    setCurrentPage(1)
  }

  const toMoney = (value: any) => Number(value || 0).toFixed(2)
  const getRentPaidFromReceipt = (receipt: any) => {
    const items = Array.isArray(receipt?.items)
      ? receipt.items
      : (receipt?.items && typeof receipt.items === 'object' ? Object.values(receipt.items) : [])
    if (items.length) {
      return items.reduce(
        (sum: number, it: any) => sum + (Number(it?.receivedAmount ?? it?.verifiedAmount ?? it?.amount ?? 0) || 0),
        0
      )
    }
    return Number(receipt?.verifiedAmount ?? receipt?.amount ?? 0) || 0
  }

  const escapeHtml = (value: any) =>
    String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')

  const downloadExcelHtml = (filename: string, html: string) => {
    const content = `
      <html>
      <head>
        <meta charset="UTF-8" />
      </head>
      <body>${html}</body>
      </html>
    `
    const blob = new Blob(['\uFEFF' + content], { type: 'application/vnd.ms-excel;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const handleExportCustomerLedger = async () => {
    try {
      const values = await customerLedgerForm.validateFields()
      const customer = String(values.customer || '').trim()
      const endDate = dayjs(values.endDate).endOf('day')
      const periodType = values.periodType as 'month' | 'year'
      const startDate = periodType === 'year'
        ? endDate.subtract(1, 'year').startOf('day')
        : endDate.subtract(1, 'month').startOf('day')

      const paidStatusSet = new Set(['cashier_done', 'accounted', 'completed'])
      const receipts = (allReceipts || [])
        .filter((r: any) => paidStatusSet.has(String(r.status || '')))
        .filter((r: any) => !customer || String(r.company || r.customerName || '').trim() === customer)
        .filter((r: any) => {
          const t = dayjs(r.verifiedAt || r.updatedAt || r.createdAt)
          return t.isValid() && !t.isBefore(startDate) && !t.isAfter(endDate)
        })
        .sort((a: any, b: any) => dayjs(a.verifiedAt || a.updatedAt || a.createdAt).valueOf() - dayjs(b.verifiedAt || b.updatedAt || b.createdAt).valueOf())

      const vehicleByPlate = new Map(vehicles.map(v => [String(v.plate || ''), v]))
      const openingReceipts = (allReceipts || [])
        .filter((r: any) => paidStatusSet.has(String(r.status || '')))
        .filter((r: any) => !customer || String(r.company || r.customerName || '').trim() === customer)
        .filter((r: any) => {
          const t = dayjs(r.verifiedAt || r.updatedAt || r.createdAt)
          return t.isValid() && t.isBefore(startDate)
        })
      const openingBalance = openingReceipts.reduce((sum: number, r: any) => {
        const amountExpected = Number(r.verifiedAmount ?? r.amount ?? 0)
        const amountPaid = getRentPaidFromReceipt(r)
        return sum + (amountExpected - amountPaid)
      }, 0)

      let runningBalance = openingBalance
      const rows = receipts.map((r: any, idx: number) => {
        const amountExpected = Number(r.verifiedAmount ?? r.amount ?? 0)
        const amountPaid = getRentPaidFromReceipt(r)
        const plate = String(r.plate || '')
        const config = vehicleByPlate.get(plate)?.configuration || vehicleByPlate.get(plate)?.notes || '-'
        runningBalance += (amountExpected - amountPaid)
        return [
          idx + 1,
          dayjs(r.verifiedAt || r.updatedAt || r.createdAt).format('YYYY-MM-DD'),
          '叉车租金',
          toMoney(amountExpected),
          toMoney(amountPaid),
          toMoney(runningBalance),
          config
        ]
      })

      const totalExpected = receipts.reduce((sum: number, r: any) => sum + Number(r.verifiedAmount ?? r.amount ?? 0), 0)
      const totalPaid = receipts.reduce((sum: number, r: any) => sum + getRentPaidFromReceipt(r), 0)
      const finalBalance = openingBalance + totalExpected - totalPaid

      const tableRows = [
        `<tr>
          <td style="border:1px solid #000;padding:6px;text-align:center;">--</td>
          <td style="border:1px solid #000;padding:6px;">${escapeHtml(startDate.format('YYYY-MM-DD'))}</td>
          <td style="border:1px solid #000;padding:6px;">期初欠款</td>
          <td style="border:1px solid #000;padding:6px;text-align:right;">--</td>
          <td style="border:1px solid #000;padding:6px;text-align:right;">--</td>
          <td style="border:1px solid #000;padding:6px;text-align:right;">${escapeHtml(toMoney(openingBalance))}</td>
          <td style="border:1px solid #000;padding:6px;">上期结转</td>
        </tr>`,
        ...rows.map((row) => `
          <tr>
            <td style="border:1px solid #000;padding:6px;text-align:center;">${escapeHtml(row[0])}</td>
            <td style="border:1px solid #000;padding:6px;">${escapeHtml(row[1])}</td>
            <td style="border:1px solid #000;padding:6px;">${escapeHtml(row[2])}</td>
            <td style="border:1px solid #000;padding:6px;text-align:right;">${escapeHtml(row[3])}</td>
            <td style="border:1px solid #000;padding:6px;text-align:right;">${escapeHtml(row[4])}</td>
            <td style="border:1px solid #000;padding:6px;text-align:right;">${escapeHtml(row[5])}</td>
            <td style="border:1px solid #000;padding:6px;">${escapeHtml(row[6])}</td>
          </tr>
        `),
        `<tr style="font-weight:700;background:#f5f5f5;">
          <td style="border:1px solid #000;padding:6px;text-align:center;">--</td>
          <td style="border:1px solid #000;padding:6px;">合计</td>
          <td style="border:1px solid #000;padding:6px;">本期汇总</td>
          <td style="border:1px solid #000;padding:6px;text-align:right;">${escapeHtml(toMoney(totalExpected))}</td>
          <td style="border:1px solid #000;padding:6px;text-align:right;">${escapeHtml(toMoney(totalPaid))}</td>
          <td style="border:1px solid #000;padding:6px;text-align:right;">${escapeHtml(toMoney(finalBalance))}</td>
          <td style="border:1px solid #000;padding:6px;">期末应收余额</td>
        </tr>`
      ].join('')

      const customerLabel = customer || '全部客户'
      const filename = `客户明细账_${customerLabel}_${startDate.format('YYYYMMDD')}_${endDate.format('YYYYMMDD')}.xls`
      const html = `
        <div style="font-family:'Microsoft YaHei',SimSun,serif;">
          <div style="font-size:18px;font-weight:700;margin-bottom:8px;">${escapeHtml(customerLabel)} 明细账</div>
          <div style="margin-bottom:10px;">期间：${escapeHtml(startDate.format('YYYY-MM-DD'))} 至 ${escapeHtml(endDate.format('YYYY-MM-DD'))}</div>
          <table style="border-collapse:collapse;width:100%;">
            <thead>
              <tr style="background:#f5f5f5;font-weight:700;">
                <th style="border:1px solid #000;padding:6px;">序号</th>
                <th style="border:1px solid #000;padding:6px;">日期</th>
                <th style="border:1px solid #000;padding:6px;">摘要</th>
                <th style="border:1px solid #000;padding:6px;">应收金额</th>
                <th style="border:1px solid #000;padding:6px;">已收金额</th>
                <th style="border:1px solid #000;padding:6px;">剩余欠款</th>
                <th style="border:1px solid #000;padding:6px;">备注</th>
              </tr>
            </thead>
            <tbody>${tableRows}</tbody>
          </table>
        </div>
      `
      downloadExcelHtml(filename, html)
      message.success('客户明细账已导出')
      setCustomerLedgerVisible(false)
    } catch (err: any) {
      if (err?.errorFields) return
      message.error(err?.message || '导出失败')
    }
  }

  const handleExportDailyReport = async () => {
    try {
      const values = await dailyReportForm.validateFields()
      const start = dayjs(values.dateRange?.[0]).startOf('day')
      const end = dayjs(values.dateRange?.[1]).endOf('day')
      const [leaseRes, receiptRes] = await Promise.all([
        simpleDirectCloudApi.getLeaseForms(),
        simpleDirectCloudApi.getReceipts()
      ])
      const leases = leaseRes.success && leaseRes.data ? leaseRes.data : []
      const receipts = receiptRes.success && receiptRes.data ? receiptRes.data : []
      const vehicleById = new Map(vehicles.map(v => [String(v._id || v.id || v.plate), v]))
      const vehicleByPlate = new Map(vehicles.map(v => [String(v.plate || ''), v]))

      const rows: Array<Array<string | number>> = []

      vehicles
        .filter(v => {
          const t = dayjs(v.createdAt)
          return t.isValid() && !t.isBefore(start) && !t.isAfter(end)
        })
        .forEach((v) => {
          rows.push([
            dayjs(v.createdAt).format('MM.DD'),
            v.type || '-',
            v.type || '-',
            v.plate || '-',
            v.company || '-',
            1,
            v.address || '-',
            '',
            '',
            '',
            '',
            '新增车辆',
            v.configuration || v.notes || '-'
          ])
        })

      leases
        .filter((l: any) => String(l.type || '') === 'return')
        .filter((l: any) => {
          const t = dayjs(l.signedAt || l.updatedAt || l.createdAt)
          return t.isValid() && !t.isBefore(start) && !t.isAfter(end)
        })
        .forEach((l: any) => {
          const vehicle = vehicleById.get(String(l.vehicleId || l._id || l.id || l.plate)) || vehicleByPlate.get(String(l.plate || ''))
          rows.push([
            dayjs(l.signedAt || l.updatedAt || l.createdAt).format('MM.DD'),
            vehicle?.type || l.model || '-',
            vehicle?.type || l.model || '-',
            l.plate || '-',
            l.unitName || l.company || '-',
            1,
            vehicle?.address || '-',
            '',
            '',
            '',
            '',
            '退租车辆',
            l.notes || '-'
          ])
        })

      leases
        .filter((l: any) => String(l.type || '') === 'relet')
        .filter((l: any) => {
          const t = dayjs(l.signedAt || l.updatedAt || l.createdAt)
          return t.isValid() && !t.isBefore(start) && !t.isAfter(end)
        })
        .forEach((l: any) => {
          const vehicle = vehicleById.get(String(l.vehicleId || l._id || l.id || l.plate)) || vehicleByPlate.get(String(l.plate || ''))
          const receivable = toMoney(l.reminderAmount ?? l.rentAmount ?? 0)
          rows.push([
            dayjs(l.signedAt || l.updatedAt || l.createdAt).format('MM.DD'),
            vehicle?.type || l.model || '-',
            vehicle?.type || l.model || '-',
            l.plate || '-',
            l.unitName || l.company || '-',
            1,
            l.address || vehicle?.address || '-',
            '',
            '',
            receivable,
            '',
            '换租车辆',
            l.notes || '-'
          ])
        })

      const paidStatusSet = new Set(['cashier_done', 'accounted', 'completed'])
      receipts
        .filter((r: any) => paidStatusSet.has(String(r.status || '')))
        .filter((r: any) => {
          const t = dayjs(r.verifiedAt || r.updatedAt || r.createdAt)
          return t.isValid() && !t.isBefore(start) && !t.isAfter(end)
        })
        .forEach((r: any) => {
          const vehicle = vehicleById.get(String(r.vehicleId || r._id || r.id || r.plate)) || vehicleByPlate.get(String(r.plate || ''))
          rows.push([
            dayjs(r.verifiedAt || r.updatedAt || r.createdAt).format('MM.DD'),
            vehicle?.type || r.model || '-',
            vehicle?.type || r.model || '-',
            r.plate || '-',
            r.company || r.customerName || '-',
            1,
            vehicle?.address || '-',
            toMoney(r.actualAmount ?? r.verifiedAmount ?? r.amount ?? 0),
            toMoney(r.verifiedAmount ?? r.amount ?? 0),
            toMoney(r.amount ?? 0),
            r.paymentMethod || '-',
            '收款',
            r.notes || '-'
          ])
        })

      rows.sort((a, b) => {
        const aNum = Number(String(a[0]).replace('.', ''))
        const bNum = Number(String(b[0]).replace('.', ''))
        return aNum - bNum
      })
      const totalActualIncome = rows
        .filter(r => String(r[11]) === '收款')
        .reduce((sum, r) => sum + Number(r[7] || 0), 0)
      rows.push(['--', '--', '--', '--', '--', '--', '--', toMoney(totalActualIncome), '--', '--', '--', '合计实收', ''])

      const title = `${start.format('YYYY')}年${start.format('MM')}月整机+仓储车销售日报表`
      const tableRows = rows.map((row) => `
        <tr>
          <td style="border:1px solid #000;padding:4px;text-align:center;">${escapeHtml(row[0])}</td>
          <td style="border:1px solid #000;padding:4px;">${escapeHtml(row[1])}</td>
          <td style="border:1px solid #000;padding:4px;">${escapeHtml(row[2])}</td>
          <td style="border:1px solid #000;padding:4px;">${escapeHtml(row[3])}</td>
          <td style="border:1px solid #000;padding:4px;">${escapeHtml(row[4])}</td>
          <td style="border:1px solid #000;padding:4px;text-align:center;">${escapeHtml(row[5])}</td>
          <td style="border:1px solid #000;padding:4px;">${escapeHtml(row[6])}</td>
          <td style="border:1px solid #000;padding:4px;text-align:right;">${escapeHtml(row[7])}</td>
          <td style="border:1px solid #000;padding:4px;text-align:right;">${escapeHtml(row[8])}</td>
          <td style="border:1px solid #000;padding:4px;text-align:right;">${escapeHtml(row[9])}</td>
          <td style="border:1px solid #000;padding:4px;">${escapeHtml(row[10])}</td>
          <td style="border:1px solid #000;padding:4px;">${escapeHtml(row[11])}</td>
          <td style="border:1px solid #000;padding:4px;">${escapeHtml(row[12])}</td>
        </tr>
      `).join('')
      const filename = `日报表_${start.format('YYYYMMDD')}_${end.format('YYYYMMDD')}.xls`
      const html = `
        <div style="font-family:'Microsoft YaHei',SimSun,serif;">
          <div style="text-align:center;font-size:20px;font-weight:700;margin-bottom:8px;">${escapeHtml(title)}</div>
          <div style="margin-bottom:10px;">统计区间：${escapeHtml(start.format('YYYY-MM-DD'))} 至 ${escapeHtml(end.format('YYYY-MM-DD'))}</div>
          <table style="border-collapse:collapse;width:100%;">
            <thead>
              <tr style="background:#f5f5f5;font-weight:700;">
                <th style="border:1px solid #000;padding:4px;">日期</th>
                <th style="border:1px solid #000;padding:4px;">商品名称</th>
                <th style="border:1px solid #000;padding:4px;">型号</th>
                <th style="border:1px solid #000;padding:4px;">编号</th>
                <th style="border:1px solid #000;padding:4px;">客户名称</th>
                <th style="border:1px solid #000;padding:4px;">数量</th>
                <th style="border:1px solid #000;padding:4px;">地址</th>
                <th style="border:1px solid #000;padding:4px;">营业额</th>
                <th style="border:1px solid #000;padding:4px;">已收款</th>
                <th style="border:1px solid #000;padding:4px;">应收款</th>
                <th style="border:1px solid #000;padding:4px;">收款方式</th>
                <th style="border:1px solid #000;padding:4px;">销售员</th>
                <th style="border:1px solid #000;padding:4px;">备注</th>
              </tr>
            </thead>
            <tbody>${tableRows}</tbody>
          </table>
        </div>
      `
      downloadExcelHtml(filename, html)
      message.success('日报表已导出')
      setDailyReportVisible(false)
    } catch (err: any) {
      if (err?.errorFields) return
      message.error(err?.message || '导出失败')
    }
  }

  const jumpToVehicleDetails = (vehicle?: Vehicle) => {
    setActiveTab('rent')
    setAreaFilter('all')
    if (vehicle) {
      const keyword = String(vehicle.plate || '').trim()
      setSearchKeyword(keyword)
      setCurrentPage(1)
    } else {
      setSearchKeyword('')
      setCurrentPage(1)
    }
    window.setTimeout(() => {
      rentDetailsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 80)
  }

  const normalizeId = (value: any) => String(value ?? '')
  const normalizeMatchKey = (value: any) => String(value ?? '').trim().toUpperCase()
  const { latestChargeRangeEndByVehicleId, latestChargeRangeEndByPlate } = useMemo(() => {
    const byVehicleId = new Map<string, string>()
    const byPlate = new Map<string, string>()
    const extractReceiptItems = (receipt: any) => {
      const raw = receipt?.items
      if (Array.isArray(raw)) return raw
      if (raw && typeof raw === 'object') return Object.values(raw)
      if (typeof raw === 'string') {
        try {
          const parsed = JSON.parse(raw)
          if (Array.isArray(parsed)) return parsed
          if (parsed && typeof parsed === 'object') return Object.values(parsed)
        } catch {
          return []
        }
      }
      return []
    }

    const vehicleIndex = new Map<string, Vehicle>()
    for (const vehicle of vehicles) {
      const keys = [vehicle._id, vehicle.id, vehicle.plate, (vehicle as any).forklift_no]
        .map(normalizeMatchKey)
        .filter(Boolean)
      for (const key of keys) vehicleIndex.set(key, vehicle)
    }

    for (const receipt of allReceipts) {
      const receiptPaid = paidStatusSet.has(String(receipt?.status || '').trim())
      const items = extractReceiptItems(receipt)
      const sourceItems = items.length ? items : [receipt]
      for (const item of sourceItems) {
        const itemPaid = paidStatusSet.has(String(item?.status || receipt?.status || '').trim())
        if (!receiptPaid && !itemPaid) continue
        const vehicleKeyCandidates = [
          item?.vehicleId,
          item?._id,
          item?.id,
          item?.plate,
          receipt?.vehicleId,
          receipt?.plate
        ]
          .map(normalizeMatchKey)
          .filter(Boolean)
        const vehicle = vehicleKeyCandidates.map((k) => vehicleIndex.get(k)).find(Boolean)
        if (!vehicle) continue

        const currentLeaseId = String((vehicle as any).currentLeaseId || '').trim()
        const itemLeaseId = String(item?.leaseId || receipt?.leaseId || '').trim()
        if (currentLeaseId && itemLeaseId && itemLeaseId !== currentLeaseId) continue

        if (!hasExplicitChargeRange(item, receipt)) continue
        const end = String(item?.chargeRangeEnd || receipt?.chargeRangeEnd || '').trim()
        if (!end || !dayjs(end).isValid()) continue
        const vehicleStart = dayjs(String(vehicle.startDate || ''))
        if (vehicleStart.isValid() && dayjs(end).isBefore(vehicleStart, 'day')) {
          continue
        }

        const vehicleId = normalizeId(vehicle._id || vehicle.id || vehicle.plate)
        const plate = String(vehicle.plate || '').trim()
        if (vehicleId) {
          const prev = byVehicleId.get(vehicleId)
          if (!prev || dayjs(end).isAfter(dayjs(prev), 'day')) {
            byVehicleId.set(vehicleId, end)
          }
        }
        if (plate) {
          const prevByPlate = byPlate.get(plate)
          if (!prevByPlate || dayjs(end).isAfter(dayjs(prevByPlate), 'day')) {
            byPlate.set(plate, end)
          }
        }
      }
    }
    return {
      latestChargeRangeEndByVehicleId: byVehicleId,
      latestChargeRangeEndByPlate: byPlate
    }
  }, [allReceipts, vehicles])
  const resolveMaker = (value?: string) =>
    makers.find(m =>
      [m._id, m.id, m.username, m.nickName].some(val => String(val || '') === String(value || ''))
    )
  const leaseMakerSeal = resolveMaker(leaseMakerId)?.makerSealUrl
  const rentAdjustMakerSeal = resolveMaker(rentAdjustMakerId)?.makerSealUrl

  const resolveSealPreviewUrl = async (fileId?: string) => {
    if (!fileId) return ''
    if (!String(fileId).startsWith('cloud://')) return String(fileId)
    await ensureLogin()
    const storage = (app as any).storage?.()
    const fileList = [{ fileID: fileId, maxAge: 24 * 60 * 60 }]
    const res: any = storage?.getTempFileURL
      ? await storage.getTempFileURL({ fileList })
      : await (app as any).getTempFileURL({ fileList })
    return (
      res?.fileList?.[0]?.tempFileURL ||
      res?.fileList?.[0]?.tempFileUrl ||
      res?.fileList?.[0]?.download_url ||
      ''
    )
  }

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const url = await resolveSealPreviewUrl(leaseMakerSeal)
        if (!cancelled) setLeaseMakerSealPreviewUrl(url)
      } catch {
        if (!cancelled) setLeaseMakerSealPreviewUrl('')
      }
    }
    load()
    return () => { cancelled = true }
  }, [leaseMakerSeal])

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const url = await resolveSealPreviewUrl(rentAdjustMakerSeal)
        if (!cancelled) setRentAdjustMakerSealPreviewUrl(url)
      } catch {
        if (!cancelled) setRentAdjustMakerSealPreviewUrl('')
      }
    }
    load()
    return () => { cancelled = true }
  }, [rentAdjustMakerSeal])

  function getOutstanding(vehicle: Vehicle) {
    if (String(vehicle?.status || '') !== 'rented') {
      const returnedRow = returnedDebtMap.get(String(vehicle?._id || vehicle?.id || vehicle?.plate || '').trim())
      if (returnedRow) return Number(returnedRow.frozenOutstanding || 0)
    }
    return (vehicle as any)._outstanding ?? 0
  }
  const getPeriodDeficits = (vehicle: Vehicle) => getPeriodDeficitsUtil(vehicle, allReceipts)
  const getPeriodLabel = (value?: string, months?: number) => formatPeriodLabel(value, months)
  const allocatePaymentByPeriod = (vehicle: Vehicle, payAmount: number, customMonths?: number) =>
    allocatePaymentByPeriodUtil(vehicle, payAmount, allReceipts, customMonths)
  const getReceiptDeadlineForVehicle = (vehicle?: Vehicle) => {
    if (!vehicle) return ''
    return (
      latestChargeRangeEndByVehicleId.get(normalizeId(vehicle._id || vehicle.id || vehicle.plate)) ||
      latestChargeRangeEndByPlate.get(String(vehicle.plate || '').trim()) ||
      String(getActiveLeaseStart(vehicle) || '')
    )
  }
  const getPreviousChargeRangeEndForVehicle = (vehicle?: Vehicle) => {
    if (!vehicle) return ''
    return (
      latestChargeRangeEndByVehicleId.get(normalizeId(vehicle._id || vehicle.id || vehicle.plate)) ||
      latestChargeRangeEndByPlate.get(String(vehicle.plate || '').trim()) ||
      ''
    )
  }
  const validateChargeRange = (vehicle: Vehicle | undefined, item: any) => {
    const messages: string[] = []
    if (!vehicle) {
      return { level: 'error' as const, messages: ['未找到车辆，无法校验收款区间'] }
    }
    if (item?.tailCollection) {
      return { level: 'ok' as const, messages: [] }
    }
    const receiptType = String(item?.receiptType || 'rent')
    if (receiptType === 'deposit_only') {
      return { level: 'ok' as const, messages: [] }
    }
    const startRaw = item?.chargeRange?.[0]?.format?.('YYYY-MM-DD')
    const endRaw = item?.chargeRange?.[1]?.format?.('YYYY-MM-DD')
    if (!startRaw && !endRaw) {
      return { level: 'ok' as const, messages: [] }
    }
    if (!startRaw || !endRaw) {
      return { level: 'error' as const, messages: ['请完整填写本次扣款范围'] }
    }
    const start = dayjs(startRaw)
    const end = dayjs(endRaw)
    if (!start.isValid() || !end.isValid()) {
      return { level: 'error' as const, messages: ['收款区间日期无效'] }
    }
    if (end.isBefore(start, 'day')) {
      return { level: 'error' as const, messages: ['收款结束日不能早于开始日'] }
    }

    const previousRangeEnd = getPreviousChargeRangeEndForVehicle(vehicle)
    const leaseStart = dayjs(String(getActiveLeaseStart(vehicle) || '').trim())
    const expectedStart = previousRangeEnd && dayjs(previousRangeEnd).isValid()
      ? dayjs(previousRangeEnd).add(1, 'day')
      : (leaseStart.isValid() ? leaseStart : null)

    if (leaseStart.isValid() && start.isBefore(leaseStart, 'day')) {
      return {
        level: 'error' as const,
        messages: [`收款开始日早于当前租期起租日 ${leaseStart.format('YYYY-MM-DD')}`],
        previousRangeEnd,
        expectedRangeStart: expectedStart?.format('YYYY-MM-DD')
      }
    }

    if (expectedStart && start.isBefore(expectedStart, 'day')) {
      return {
        level: 'error' as const,
        messages: [`收款区间与历史记录重叠，上一笔截止到 ${expectedStart.subtract(1, 'day').format('YYYY-MM-DD')}`],
        previousRangeEnd,
        expectedRangeStart: expectedStart.format('YYYY-MM-DD')
      }
    }

    if (expectedStart && start.isAfter(expectedStart, 'day')) {
      messages.push(`收款区间不连续，${expectedStart.format('YYYY-MM-DD')} 至 ${start.subtract(1, 'day').format('YYYY-MM-DD')} 尚未覆盖`)
    }

    const dueRaw = getVehicleDueDate(vehicle)
    const due = dueRaw && dayjs(dueRaw).isValid() ? dayjs(dueRaw) : null
    if (due && end.isAfter(due, 'day')) {
      messages.push(`收款结束日超过当前收款截止日期 ${due.format('YYYY-MM-DD')}`)
    }

    return {
      level: messages.length ? 'warning' as const : 'ok' as const,
      messages,
      previousRangeEnd,
      expectedRangeStart: expectedStart?.format('YYYY-MM-DD')
    }
  }
  const resolveVehicleByAnyId = (value: any) => {
    const target = String(value ?? '').trim().toUpperCase()
    if (!target) return undefined
    return vehicles.find(v => {
      const keys = [v._id, v.id, v.plate, (v as any).forklift_no].map(k => String(k ?? '').trim().toUpperCase())
      return keys.includes(target)
    })
  }
  const handleVehicleSelectionChange = (ids: string[]) => {
    const normalizedIds = ids.map(normalizeId).filter(Boolean)
    console.log('[VehicleManager] selection ids', ids, 'normalized', normalizedIds)
    const selected = vehicles.filter(v => normalizedIds.includes(normalizeId(v._id || v.id || v.plate)))
    console.log('[VehicleManager] selected vehicles', selected.map(v => ({
      id: v._id || v.id || v.plate,
      plate: v.plate
    })))
    setSelectedVehicles(selected)

    const existingItems: any[] = form.getFieldValue('items') || []
    const nextItems = normalizedIds.map((id: string) => {
      const v = selected.find(item => normalizeId(item._id || item.id || item.plate) === id)
      const keep = existingItems.find(it => normalizeId(it.vehicleId) === id)
      if (!v && !keep) {
        console.warn('[VehicleManager] no match for id', id)
      }
      if (keep) {
        return {
          ...keep,
          receiptType: keep.receiptType || 'rent'
        }
      }
      return {
        vehicleId: id,
        amount: v?.rentAmount || 0,
        actualAmount: 0,
        depositAmount: 0,
        receiptType: 'rent',
        dueDate: v?.dueDate ? dayjs(v.dueDate) : null,
        startDate: v?.startDate ? dayjs(v.startDate) : null,
        rentPeriod: 'yearly',
        rentPeriodMonths: undefined
      }
    })
    console.log('[VehicleManager] nextItems', nextItems)
    form.setFieldsValue({ vehicleIds: normalizedIds, items: nextItems.filter(it => normalizeId(it.vehicleId)) })
  }

  const handleOpenRentForm = (vehicle?: Vehicle | null, options?: { tailCollectionRow?: any }) => {
    const target = vehicle || null
    const debtRow = options?.tailCollectionRow || null
    if (!debtRow) setTailCollectionRow(null)
    if (target?.returnSigned && !isReceivableVehicleStatus(target?.status) && !debtRow) {
      message.error('该车辆已退租，不能再收款')
      return
    }
    form.resetFields()
    const makerMatch = makers.find(m =>
      [m._id, m.id, m.username, m.nickName].some(val => String(val || '') === String(user?.id || user?.nickName || ''))
    )
    const defaultMakerId = makerMatch
      ? String(makerMatch._id || makerMatch.id || makerMatch.username || makerMatch.nickName)
      : undefined
    const vehicleId = target?._id || target?.id || target?.plate
      ? normalizeId(target?._id || target?.id || target?.plate)
      : ''
    const ids = vehicleId ? [vehicleId] : []
    const initialItems = vehicleId
      ? [
          {
            vehicleId,
            amount: debtRow ? Number(debtRow.frozenOutstanding || 0) : (target?.rentAmount || 0),
            actualAmount: debtRow ? Number(debtRow.frozenOutstanding || 0) : 0,
            depositAmount: 0,
            receiptType: 'rent',
            dueDate: target?.dueDate ? dayjs(target.dueDate) : null,
            startDate: target?.startDate ? dayjs(target.startDate) : null,
            chargeRange: debtRow?.debtPeriodLabel?.includes('~')
              ? [
                  dayjs(String(debtRow.debtPeriodLabel).split('~')[0].trim()),
                  dayjs(String(debtRow.debtPeriodLabel).split('~')[1].trim())
                ]
              : undefined,
            tailCollection: Boolean(debtRow),
            rentPeriod: 'yearly',
            rentPeriodMonths: undefined
          }
        ]
      : []
    setSelectedVehicles(ids.length ? [target as Vehicle] : [])
    form.setFieldsValue({
      vehicleIds: ids,
      items: initialItems,
      notes: '',
      paymentDate: dayjs(),
      accountingScenario: 'auto',
      tradeInAmount: 0,
      otherIncomeAmount: 0,
      totalAmount: undefined,
      mergeByCompany: false,
      makerId: defaultMakerId
    })
    setTailCollectionRow(debtRow)
    setMergeByCompany(false)
    setFileList([])
    setRentModalVisible(true)
    if (ids.length) {
      window.setTimeout(() => handleVehicleSelectionChange(ids), 0)
    }
  }

  useEffect(() => {
    if (!rentModalVisible) return
    const ids: string[] = (form.getFieldValue('vehicleIds') || []).map((id: any) => normalizeId(id)).filter(Boolean)
    if (ids.length) {
      const itemsNow: any[] = form.getFieldValue('items') || []
      const hasMissingVehicleId = itemsNow.some((item: any) => !normalizeId(item?.vehicleId))
      if (hasMissingVehicleId || itemsNow.length !== ids.length) {
        handleVehicleSelectionChange(ids)
      }
    }
  }, [rentModalVisible, form, vehicles])

  const handleSubmitLeaseForm = async () => {
    try {
      const values = await leaseForm.validateFields()
      const vehicleId = values.vehicleId
      const vehicle = vehicles.find(v => String(v._id || v.id || v.plate) === String(vehicleId))
      if (!vehicle) {
        message.error('车辆不存在')
        return
      }
      const type = values.type
      const unitName = values.unitName
      const notes = values.notes || ''
      const startDate =
        type === 'relet' && values.startDate
          ? dayjs(values.startDate).format('YYYY-MM-DD')
          : undefined
      const dueDate =
        type === 'relet' && values.dueDate
          ? dayjs(values.dueDate).format('YYYY-MM-DD')
          : undefined
      const rentPeriod = type === 'relet' ? (values.rentPeriod || 'yearly') : undefined
      const rentPeriodMonths = rentPeriod === 'custom' ? Number(values.rentPeriodMonths || 1) : undefined
      const reminderPeriod = type === 'relet' ? (values.reminderPeriod || rentPeriod || 'monthly') : undefined
      const reminderPeriodMonths = reminderPeriod === 'custom' ? Number(values.reminderPeriodMonths || 1) : undefined
      const reminderAmount = type === 'relet' ? Number(values.reminderAmount ?? values.rentAmount ?? vehicle.rentAmount ?? 0) : undefined
      const receivableMode = type === 'relet' ? (values.receivableMode || 'daily_365') : undefined
      const receivableMonths = receivableMode === 'monthly_plan' ? Number(values.receivableMonths || 12) : undefined
      const returnTime =
        type === 'return' && values.returnTime
          ? dayjs(values.returnTime).toISOString()
          : undefined
      const makerProfile = resolveMaker(values.makerId)
      const makerName = makerProfile?.nickName || user?.nickName || user?.id
      const res = await simpleDirectCloudApi.addLeaseForm({
        type,
        vehicleId: vehicle._id || vehicle.id || vehicle.plate,
        plate: vehicle.plate,
        model: vehicle.type,
        configuration: vehicle.configuration || vehicle.notes || '',
        company: vehicle.company,
        unitName,
        returnTime,
        startDate,
        dueDate,
        rentAmount: type === 'relet' ? Number(values.rentAmount || 0) : undefined,
        rentPeriod,
        rentPeriodMonths,
        reminderPeriod,
        reminderPeriodMonths,
        reminderAmount,
        receivableMode,
        receivableMonths,
        address: type === 'relet' ? values.address : undefined,
        notes,
        vehicleSnapshotBefore: buildLeaseFormVehicleSnapshot(vehicle),
        createdBy: makerName,
        makerId: makerProfile?._id || makerProfile?.id || makerProfile?.username,
        makerSealUrl: makerProfile?.makerSealUrl
      })
      if (res.success) {
        message.success('已提交，等待出纳核验')
        setLeaseModalVisible(false)
        leaseForm.resetFields()
      } else {
        message.error(res.error || '提交失败')
      }
    } catch (err: any) {
      message.error(err?.message || '提交失败')
    }
  }

  const handleSubmitRentAdjustRequest = async () => {
    try {
      const values = await rentAdjustForm.validateFields()
      const vehicleId = values.vehicleId
      const vehicle = vehicles.find(v => String(v._id || v.id || v.plate) === String(vehicleId))
      if (!vehicle) {
        message.error('车辆不存在')
        return
      }
      const makerProfile = resolveMaker(values.makerId)
      const makerName = makerProfile?.nickName || user?.nickName || user?.id
      const attachmentUrls: string[] = []
      for (const file of rentAdjustFileList) {
        if (!file.originFileObj) continue
        const cloudPath = `rent_adjust_requests/${vehicle.plate || vehicleId}-${Date.now()}-${file.name}`
        const uploadRes: any = await app.uploadFile({
          cloudPath,
          filePath: file.originFileObj
        } as any)
        if (uploadRes?.fileID) attachmentUrls.push(uploadRes.fileID)
      }
      const res = await simpleDirectCloudApi.addRentAdjustRequest({
        vehicleId: vehicle._id || vehicle.id || vehicle.plate,
        plate: vehicle.plate,
        model: vehicle.type,
        configuration: vehicle.configuration || vehicle.notes || '',
        company: vehicle.company,
        currentRent: Number(vehicle.rentAmount || 0),
        notes: values.notes || '',
        attachmentUrls,
        status: 'filed',
        createdBy: makerName,
        makerId: makerProfile?._id || makerProfile?.id || makerProfile?.username,
        makerSealUrl: makerProfile?.makerSealUrl
      })
      if (res.success) {
        message.success('已提交并归档')
        setRentAdjustModalVisible(false)
        rentAdjustForm.resetFields()
        setRentAdjustFileList([])
        loadData()
      } else {
        message.error(res.error || '提交失败')
      }
    } catch (err: any) {
      message.error(err?.message || '提交失败')
    }
  }
  const handlePreviewRentForm = async () => {
    try {
      const values = await form.validateFields()
      const idsFromForm: string[] = values.vehicleIds || []
      const fallbackIds = selectedVehicles.map(v => v._id || v.id || v.plate).filter(Boolean) as string[]
      const itemIds = (values.items || []).map((item: any) => item?.vehicleId).filter(Boolean)
      const ids: string[] = idsFromForm.length ? idsFromForm : (fallbackIds.length ? fallbackIds : itemIds)
      let items = values.items || []
      if (!ids.length) {
        message.error('请选择车辆')
        return
      }
      if (values.mergeByCompany) {
        const total = Number(values.totalAmount || 0)
        if (!total || total <= 0) {
          message.error('请输入正确的金额')
          return
        }
      }
      if (!items.length) {
        message.error('请选择车辆')
        return
      }
      const preview: PreviewItem[] = items.map((item: any) => {
        const vehicle =
          selectedVehicles.find(v => normalizeId(v._id || v.id || v.plate) === normalizeId(item.vehicleId)) ||
          vehicles.find(v => normalizeId(v._id || v.id || v.plate) === normalizeId(item.vehicleId))!
        const chargeRangeStart = item.chargeRange?.[0]?.format?.('YYYY-MM-DD')
        const chargeRangeEnd = item.chargeRange?.[1]?.format?.('YYYY-MM-DD')
        const rangeValidation = validateChargeRange(vehicle, item)
        return {
          vehicle,
          amount: Number(item.amount || 0),
          actualAmount: Number(item.actualAmount || 0),
          depositAmount: Number(item.depositAmount || 0),
          receiptType: item.receiptType || (Number(item.amount || 0) <= 0 && Number(item.depositAmount || 0) > 0 ? 'deposit_only' : (Number(item.depositAmount || 0) > 0 ? 'rent_with_deposit' : 'rent')),
          dueDate: item.dueDate ? item.dueDate.format('YYYY-MM-DD') : getVehicleDueDate(vehicle),
          startDate: item.startDate ? item.startDate.format('YYYY-MM-DD') : vehicle?.startDate,
          rentPeriod: 'yearly',
          rentPeriodMonths: undefined,
          chargeRangeStart,
          chargeRangeEnd,
          outstanding: getOutstanding(vehicle),
          rangeValidation
        }
      })
      const blockingErrors = preview
        .filter(item => item.rangeValidation?.level === 'error')
        .flatMap(item => {
          const plate = item.vehicle?.plate || '未知车辆'
          return (item.rangeValidation?.messages || []).map(msg => `${plate}：${msg}`)
        })
      if (blockingErrors.length) {
        Modal.error({
          title: '收款区间校验未通过',
          width: 640,
          content: (
            <div>
              {blockingErrors.map((msg, idx) => (
                <div key={idx} style={{ marginBottom: 8 }}>{msg}</div>
              ))}
            </div>
          )
        })
        return
      }
    setPendingValues({ ...values, vehicleIds: ids, mergeByCompany: values.mergeByCompany ?? mergeByCompany, items })
      setPreviewItems(preview)
      setPreviewVisible(true)
    } catch {
      message.error('预览失败')
    }
  }

  const uploadEvidence = async (vehicleTag: string) => {
    const evidenceUrls: string[] = []
    for (const file of fileList) {
      if (!file.originFileObj) continue
      const cloudPath = `receipts/${vehicleTag}-${Date.now()}-${file.name}`
      const res: any = await app.uploadFile({
        cloudPath,
        filePath: file.originFileObj
      } as any)
      if (res?.fileID) {
        evidenceUrls.push(res.fileID)
      }
    }
    return evidenceUrls
  }

  const openCloudFile = async (fileId: string) => {
    try {
      if (!fileId) return
      const url = await resolveCloudFileUrl(fileId)
      if (!url) {
        message.error('文件链接无效')
        return
      }
      window.open(url, '_blank')
    } catch (err) {
      console.error(err)
      message.error('打开文件失败')
    }
  }

  const resolveCloudFileUrl = async (fileId?: string) => {
    if (!fileId) return ''
    if (!String(fileId).startsWith('cloud://')) return String(fileId)
    await ensureLogin()
    const storage = (app as any).storage?.()
    const fileList = [{ fileID: fileId, maxAge: 24 * 60 * 60 }]
    const res: any = storage?.getTempFileURL
      ? await storage.getTempFileURL({ fileList })
      : await (app as any).getTempFileURL({ fileList })
    return (
      res?.fileList?.[0]?.tempFileURL ||
      res?.fileList?.[0]?.tempFileUrl ||
      res?.fileList?.[0]?.download_url ||
      ''
    )
  }

  const signedReceiptDocs = useMemo(() => {
    const doneStatuses = ['cashier_done', 'accounted', 'completed']
    return allReceipts
      .filter((r: any) => doneStatuses.includes(String(r.status || '')))
      .filter((r: any) => {
        if (r.cashierSignatureUrl) return true
        const items = getReceiptItems(r)
        return items.some((it: any) => it.cashierSignatureUrl)
      })
      .sort((a: any, b: any) => dayjs(b.verifiedAt || b.updatedAt || b.createdAt).valueOf() - dayjs(a.verifiedAt || a.updatedAt || a.createdAt).valueOf())
  }, [allReceipts])

  const filteredSignedReceiptDocs = useMemo(() => {
    const keyword = String(signedDocSearchKeyword || '').trim().toLowerCase()
    if (!keyword) return signedReceiptDocs
    return signedReceiptDocs.filter((receipt: any) => {
      const texts = [
        receipt?.plate,
        receipt?.company,
        receipt?.createdBy,
        receipt?.cashierName,
        receipt?.notes,
        receipt?._id,
        receipt?.id
      ].map(v => String(v || '').toLowerCase())
      return texts.some(t => t.includes(keyword))
    })
  }, [signedReceiptDocs, signedDocSearchKeyword])

  const filteredSignedLeaseForms = useMemo(() => {
    const keyword = String(signedDocSearchKeyword || '').trim().toLowerCase()
    if (!keyword) return signedLeaseForms
    return signedLeaseForms.filter((doc: any) => {
      const texts = [
        doc?.plate,
        doc?.company,
        doc?.unitName,
        doc?.createdBy,
        doc?.cashierName,
        doc?.notes,
        doc?._id,
        doc?.id
      ].map(v => String(v || '').toLowerCase())
      return texts.some(t => t.includes(keyword))
    })
  }, [signedLeaseForms, signedDocSearchKeyword])

  const printHtml = (title: string, body: string) => {
    const html = `
      <html>
        <head>
          <title>${title}</title>
          <style>
            body { margin: 24px; font-family: "SimSun","STSong","Noto Serif SC",serif; color: #222; }
            table { width: 100%; border-collapse: collapse; margin-top: 12px; table-layout: fixed; }
            th, td { border: 1px solid #333; padding: 8px 10px; text-align: left; vertical-align: top; line-height: 1.6; word-break: break-word; overflow-wrap: anywhere; }
            th { font-weight: 700; vertical-align: middle; }
            .center { text-align: center; }
            .right { text-align: right; }
            .nowrap { white-space: nowrap; }
            .item-name { padding-left: 14px; }
            .period-cell { line-height: 1.5; }
            .row { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; margin-top: 6px; }
            img { max-width: 220px; max-height: 120px; border: 1px solid #ddd; border-radius: 4px; }
          </style>
        </head>
        <body>${body}</body>
      </html>
    `

    const iframe = document.createElement('iframe')
    iframe.style.position = 'fixed'
    iframe.style.right = '0'
    iframe.style.bottom = '0'
    iframe.style.width = '0'
    iframe.style.height = '0'
    iframe.style.border = '0'
    iframe.setAttribute('aria-hidden', 'true')
    document.body.appendChild(iframe)

    const cleanup = () => {
      setTimeout(() => {
        try { document.body.removeChild(iframe) } catch {}
      }, 300)
    }

    iframe.onload = () => {
      try {
        const frameWindow = iframe.contentWindow
        if (!frameWindow) {
          cleanup()
          return
        }
        frameWindow.focus()
        frameWindow.print()
      } finally {
        cleanup()
      }
    }

    const doc = iframe.contentDocument || iframe.contentWindow?.document
    if (!doc) {
      cleanup()
      return
    }
    doc.open()
    doc.write(html)
    doc.close()
  }

  const handlePrintReceipt = async (receipt: any) => {
    try {
      const items = getReceiptItems(receipt)
      const company = receipt.company || receipt.customerName || '-'
      const receiptNo = receipt._id || receipt.id || '-'
      const date = dayjs(receipt.verifiedAt || receipt.createdAt).isValid()
        ? dayjs(receipt.verifiedAt || receipt.createdAt).format('YYYY-MM-DD')
        : '-'
      const makerName = receipt.createdBy || '-'
      const cashierName = receipt.cashierName || '-'
      const total = items.reduce((sum: number, it: any) => sum + Number(it.receivedAmount ?? it.expectedAmount ?? it.amount ?? 0), 0)
      const depositTotal = Number(
        receipt.depositAmount ??
        items.reduce((sum: number, it: any) => sum + (Number(it.depositAmount || 0) || 0), 0) ??
        0
      ) || 0
      const actualIncome = Number(receipt.actualAmount ?? total + depositTotal) || 0
      const signatureUrl = await resolveCloudFileUrl(receipt.cashierSignatureUrl || items.find((it: any) => it.cashierSignatureUrl)?.cashierSignatureUrl)
      const makerSealUrl = await resolveCloudFileUrl(receipt.makerSealUrl)
      const rows = items.map((it: any) => {
        const explicitRange = hasExplicitChargeRange(it, receipt)
        const rangeStart = explicitRange ? (it.chargeRangeStart || receipt.chargeRangeStart || '') : ''
        const rangeEnd = explicitRange ? (it.chargeRangeEnd || receipt.chargeRangeEnd || '') : ''
        const periodLabel = rangeStart && rangeEnd ? `${rangeStart} ~ ${rangeEnd}` : ''
        const amount = Number(it.receivedAmount ?? it.expectedAmount ?? it.amount ?? 0)
        if (Math.abs(amount) < 0.005) return ''
        return `
          <tr>
            <td class="center"></td>
            <td class="item-name">车辆租金（${it.plate || receipt.plate || '-'}）</td>
            <td class="period-cell">${periodLabel}</td>
            <td class="center nowrap">台</td>
            <td class="center nowrap">1</td>
            <td class="right">${amount.toFixed(2)}</td>
            <td class="right">${amount.toFixed(2)}</td>
          </tr>
        `
      }).join('')
      const depositRow = depositTotal > 0 ? `
          <tr>
            <td class="center"></td>
            <td class="item-name">押金（${receipt.plate || '-' }）</td>
            <td class="center">-</td>
            <td class="center nowrap">笔</td>
            <td class="center nowrap">1</td>
            <td class="right">${depositTotal.toFixed(2)}</td>
            <td class="right">${depositTotal.toFixed(2)}</td>
          </tr>
      ` : ''
      const tableRows = `${rows}${depositRow}` || `
          <tr>
            <td class="center"></td><td class="item-name">押金</td><td class="center">-</td><td class="center nowrap">笔</td><td class="center nowrap">1</td><td class="right">0.00</td><td class="right">0.00</td>
          </tr>
      `
      const receiptTitle = String(receipt.receiptType || '') === 'deposit_only' ? '押金收据' : '收款收据'
      printHtml(receiptTitle, `
        <div style="text-align:center;font-size:28px;font-weight:700;letter-spacing:8px;">${receiptTitle}</div>
        <div class="row"><div>客户名称：${company}</div><div>No：${receiptNo}</div></div>
        <div class="row"><div>日期：${date}</div><div>制单人：${makerName}</div></div>
        <table>
          <colgroup>
            <col style="width:8%">
            <col style="width:24%">
            <col style="width:24%">
            <col style="width:8%">
            <col style="width:8%">
            <col style="width:14%">
            <col style="width:14%">
          </colgroup>
          <thead>
            <tr>
              <th class="center">货号</th><th>品名及规格</th><th>收款区间</th><th class="center">单位</th><th class="center">数量</th><th class="right">单价</th><th class="right">金额</th>
            </tr>
          </thead>
          <tbody>${tableRows}</tbody>
        </table>
        <div class="row"><div>合计人民币：${total.toFixed(2)}</div><div>￥${total.toFixed(2)}</div></div>
        <div class="row"><div>押金：${depositTotal.toFixed(2)}</div><div>￥${depositTotal.toFixed(2)}</div></div>
        <div class="row"><div>实际收入：${actualIncome.toFixed(2)}</div><div>￥${actualIncome.toFixed(2)}</div></div>
        <div class="row"><div>制单人：${makerName}</div><div>出纳：${cashierName}</div></div>
        ${makerSealUrl ? `<div style="margin-top:8px;text-align:right;"><img src="${makerSealUrl}" /></div>` : ''}
        ${signatureUrl ? `<div style="margin-top:8px;text-align:right;"><img src="${signatureUrl}" /></div>` : ''}
        <div style="margin-top:8px;">备注：${receipt.cashierNotes || receipt.notes || '-'}</div>
      `)
    } catch (err) {
      console.error(err)
      message.error('打印收据失败')
    }
  }

  const handlePrintLeaseForm = async (doc: any) => {
    try {
      const signatureUrl = await resolveCloudFileUrl(doc.cashierSignatureUrl)
      const makerSealUrl = await resolveCloudFileUrl(doc.makerSealUrl)
      const title = doc.type === 'return' ? '退租单' : '出租单'
      const unitLabel = doc.type === 'relet' ? '出租单位' : '退租单位'
      const docDate = doc.type === 'return'
        ? (doc.returnTime ? dayjs(doc.returnTime).format('YYYY-MM-DD HH:mm') : '-')
        : (doc.createdAt ? dayjs(doc.createdAt).format('YYYY-MM-DD') : '-')
      printHtml(title, `
        <div style="text-align:center;font-size:28px;font-weight:700;letter-spacing:8px;">${title}</div>
        <div class="row"><div>${unitLabel}：${doc.unitName || '-'}</div><div>No：${doc._id || doc.id || '-'}</div></div>
        <div class="row"><div>车牌：${doc.plate || '-'}</div><div>${doc.type === 'return' ? '退租时间' : '日期'}：${docDate}</div></div>
        <table>
          <thead>
            <tr>
              <th>货号</th><th>品名及规格</th><th>${unitLabel}</th><th>单位</th><th>数量</th><th>单价</th><th>金额</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td></td>
              <td>车辆租赁（${doc.plate || '-'}）${doc.model ? ` - ${doc.model}` : ''}</td>
              <td>${doc.unitName || '-'}</td>
              <td>台</td>
              <td>1</td>
              <td class="right">-</td>
              <td class="right">-</td>
            </tr>
          </tbody>
        </table>
        <div style="margin-top:8px;">配置：${doc.configuration || '-'}</div>
        <div class="row"><div>备注：${doc.notes || '-'}</div><div>状态：${doc.status === 'signed' ? '已签名' : '待签名'}</div></div>
        <div class="row"><div>制单人：${doc.createdBy || '-'}</div><div>出纳：${doc.cashierName || '-'}</div></div>
        ${makerSealUrl ? `<div style="margin-top:8px;text-align:right;"><img src="${makerSealUrl}" /></div>` : ''}
        ${signatureUrl ? `<div style="margin-top:8px;text-align:right;"><img src="${signatureUrl}" /></div>` : ''}
      `)
    } catch (err) {
      console.error(err)
      message.error('打印单据失败')
    }
  }
  const handleCancelLeaseForm = (doc: any) => {
    const isReturn = doc.type === 'return'
    Modal.confirm({
      title: `确认撤销${isReturn ? '退租单' : '出租单'}？`,
      content: isReturn
        ? `将删除 ${doc.plate || ''} 的退租单，并将车辆状态恢复为出租中。此操作不可撤回。`
        : `将删除 ${doc.plate || ''} 的出租单，并同步回滚车辆资料。此操作不可撤回。`,
      okText: '确认撤销',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        const vehicleId = doc.vehicleId || vehicles.find(v => v.plate === doc.plate)?._id || vehicles.find(v => v.plate === doc.plate)?.id
        const res = await simpleDirectCloudApi.deleteLeaseForm(
          doc._id || doc.id,
          isReturn,
          vehicleId
        )
        if (res.success) {
          message.success('撤销成功')
          loadData()
        } else {
          message.error(res.error || '撤销失败')
        }
      }
    })
  }

  const handleConfirmSubmit = async () => {
    if (submittingReceipt) return
    if (!pendingValues) {
      setPreviewVisible(false)
      return
    }
      const {
        items: pendingItems = [],
      notes = '',
      mergeByCompany: mergeFlagRaw,
      totalAmount,
      vehicleIds: pendingIds,
      makerId,
      paymentDate,
      accountingScenario = 'auto',
      tradeInAmount = 0,
      otherIncomeAmount = 0
    } = pendingValues
      const mergeFlag = mergeFlagRaw ?? mergeByCompany
    let items = pendingItems
    const totalDeposit = items.reduce((sum: number, item: any) => sum + Number(item.depositAmount || 0), 0)
    const totalActual = items.reduce((sum: number, item: any) => sum + Number(item.actualAmount || 0), 0) + totalDeposit
    const totalReceiptActual = totalActual + Number(tradeInAmount || 0) + Number(otherIncomeAmount || 0)
    const makerProfile = resolveMaker(makerId)
    const makerName = makerProfile?.nickName || user?.nickName || user?.id
    const makerIdValue = makerProfile?._id || makerProfile?.id || makerProfile?.username
    const makerSealUrl = makerProfile?.makerSealUrl
    const targetVehicles = (pendingIds && pendingIds.length ? pendingIds : selectedVehicles.map(v => v._id || v.id || v.plate)).map((id: string) =>
      vehicles.find(v => (v._id || v.id || v.plate) === id)
    ).filter(Boolean) as Vehicle[]
    const applyDepositToVehicle = async (vehicle: Vehicle | undefined, deposit: number) => {
      if (!vehicle) return
      const delta = Number(deposit || 0)
      if (!delta) return
      const docId = String(vehicle._id || vehicle.id || vehicle.plate || '')
      if (!docId) return
      const currentDeposit = Number(vehicle.depositAmount || 0)
      await simpleDirectCloudApi.updateVehicle(docId, {
        _id: docId,
        depositAmount: Number((currentDeposit + delta).toFixed(2))
      } as any)
    }

    try {
      setSubmittingReceipt(true)
      if (mergeFlag) {
        const total = totalAmount ? Number(totalAmount) : items.reduce((s: number, it: any) => s + Number(it.amount || 0), 0)
        const mergeReceiptType = total <= 0 && totalDeposit > 0 ? 'deposit_only' : (totalDeposit > 0 ? 'rent_with_deposit' : 'rent')
        const itemsForSplit = items
        const allocatedItems: any[] = itemsForSplit.map((item: any) => {
          const vehicle = resolveVehicleByAnyId(item.vehicleId) || targetVehicles.find(v => normalizeId(v._id || v.id || v.plate) === normalizeId(item.vehicleId))
          const startDate = item.startDate ? dayjs(item.startDate).format('YYYY-MM-DD') : vehicle?.startDate
          const dueDate = item.dueDate ? dayjs(item.dueDate).format('YYYY-MM-DD') : vehicle?.dueDate
          const rangeStart = item.chargeRange?.[0] ? dayjs(item.chargeRange[0]).format('YYYY-MM-DD') : undefined
          const rangeEnd = item.chargeRange?.[1] ? dayjs(item.chargeRange[1]).format('YYYY-MM-DD') : undefined
          const explicitChargeRange = Boolean(rangeStart && rangeEnd)
          return {
            vehicleId: vehicle?._id || vehicle?.id || item.vehicleId,
            leaseId: (vehicle as any)?.currentLeaseId,
            plate: vehicle?.plate || item.plate,
            expectedAmount: Number(item.amount || 0),
            receivedAmount: Number(item.amount || 0),
            actualAmountDraft: Number(item.actualAmount || 0),
            depositAmount: Number(item.depositAmount || 0),
            periodKey: 'merge',
            periodStart: startDate || '',
            periodEnd: dueDate || '',
            dueDate: dueDate || '',
            startDate: startDate || '',
            rentPeriod: 'yearly',
            status: 'submitted',
            hasExplicitChargeRange: explicitChargeRange,
            chargeRangeStart: explicitChargeRange ? rangeStart : undefined,
            chargeRangeEnd: explicitChargeRange ? rangeEnd : undefined
          }
        })
        for (const item of itemsForSplit) {
          const vehicle = resolveVehicleByAnyId(item.vehicleId) || targetVehicles.find(v => normalizeId(v._id || v.id || v.plate) === normalizeId(item.vehicleId))
          await applyDepositToVehicle(vehicle, Number(item.depositAmount || 0))
        }

        const first = targetVehicles[0] || vehicles.find(v => (v._id || v.id || v.plate) === items[0]?.vehicleId)
        const evidenceUrls = await uploadEvidence(first?.plate || 'merged')
        const mergeRangeStart = itemsForSplit.find((it: any) => it.chargeRange?.[0])?.chargeRange?.[0]
        const mergeRangeEnd = itemsForSplit.find((it: any) => it.chargeRange?.[1])?.chargeRange?.[1]
        await simpleDirectCloudApi.addReceipt({
          vehicleId: first?._id || first?.id || first?.plate,
          plate: first?.plate || '合并收款',
          model: first?.type,
          company: first?.company,
          customerName: first?.company,
          amount: total,
          actualAmountDraft: totalReceiptActual ? Number(totalReceiptActual) : undefined,
          depositAmount: totalDeposit || undefined,
          dueDate: first?.dueDate,
          startDate: first?.startDate,
          rentPeriod: 'yearly',
          status: 'submitted',
          receiptType: mergeReceiptType,
          createdBy: makerName,
          makerId: makerIdValue,
          makerSealUrl,
          notes: notes || '',
          paymentDate: paymentDate ? dayjs(paymentDate).format('YYYY-MM-DD') : undefined,
          accountingScenario,
          tradeInAmount: Number(tradeInAmount || 0) || undefined,
          otherIncomeAmount: Number(otherIncomeAmount || 0) || undefined,
          evidenceUrls,
          items: allocatedItems,
          mergeByCompany: true,
          chargeRangeStart: mergeRangeStart ? dayjs(mergeRangeStart).format('YYYY-MM-DD') : undefined,
          chargeRangeEnd: mergeRangeEnd ? dayjs(mergeRangeEnd).format('YYYY-MM-DD') : undefined
        })
      } else {
        for (const item of items) {
          const vehicle = resolveVehicleByAnyId(item.vehicleId) || selectedVehicles.find(v => normalizeId(v._id || v.id || v.plate) === normalizeId(item.vehicleId))
          if (!vehicle) continue
          const docId = vehicle._id || vehicle.id || item.vehicleId
          const selectedTypeRaw = String(item.receiptType || '').trim()
          const selectedType = ['rent', 'deposit_only', 'rent_with_deposit'].includes(selectedTypeRaw) ? selectedTypeRaw as 'rent' | 'deposit_only' | 'rent_with_deposit' : undefined
          let payAmount = Number(item.amount || vehicle.rentAmount || 0)
          const itemActual = Number(item.actualAmount || 0)
          let itemDeposit = Number(item.depositAmount || 0)
          if (selectedType === 'deposit_only') {
            payAmount = 0
          }
          if (selectedType === 'rent') {
            itemDeposit = 0
          }
          const itemReceiptType = selectedType || (payAmount <= 0 && itemDeposit > 0 ? 'deposit_only' : (itemDeposit > 0 ? 'rent_with_deposit' : 'rent'))
          const rangeStart = item.chargeRange?.[0] ? dayjs(item.chargeRange[0]).format('YYYY-MM-DD') : undefined
          const rangeEnd = item.chargeRange?.[1] ? dayjs(item.chargeRange[1]).format('YYYY-MM-DD') : undefined
          const explicitChargeRange = Boolean(rangeStart && rangeEnd)
          const allocatedItems = item.tailCollection
            ? [{
                vehicleId: docId,
                leaseId: (vehicle as any)?.currentLeaseId,
                plate: vehicle.plate,
                model: vehicle.type,
                company: vehicle.company,
                expectedAmount: payAmount,
                receivedAmount: payAmount,
                actualAmount: itemActual || payAmount,
                dueDate: rangeEnd || vehicle.dueDate,
                startDate: rangeStart || vehicle.startDate || '',
                rentPeriod: vehicle.rentPeriod || 'monthly',
                status: 'submitted',
                hasExplicitChargeRange: explicitChargeRange,
                chargeRangeStart: explicitChargeRange ? rangeStart : undefined,
                chargeRangeEnd: explicitChargeRange ? rangeEnd : undefined
              }]
            : allocatePaymentByPeriod(
                vehicle,
                payAmount,
                undefined
              )
          const allocatedWithRange = allocatedItems.map((it: any) => ({
            ...it,
            depositAmount: itemDeposit,
            hasExplicitChargeRange: explicitChargeRange,
            chargeRangeStart: explicitChargeRange ? rangeStart : undefined,
            chargeRangeEnd: explicitChargeRange ? rangeEnd : undefined
          }))

          const evidenceUrls = await uploadEvidence(vehicle.plate || docId)
          await applyDepositToVehicle(vehicle, itemDeposit)

          await simpleDirectCloudApi.addReceipt({
            vehicleId: docId,
            plate: vehicle.plate,
            model: vehicle.type,
            company: vehicle.company,
            customerName: vehicle.company,
            amount: payAmount,
            actualAmountDraft: (itemActual || itemDeposit) ? Number(itemActual + itemDeposit) : undefined,
            depositAmount: itemDeposit || undefined,
            dueDate: item.dueDate ? dayjs(item.dueDate).format('YYYY-MM-DD') : vehicle.dueDate,
            startDate: item.startDate ? dayjs(item.startDate).format('YYYY-MM-DD') : vehicle.startDate,
            rentPeriod: 'yearly',
            status: 'submitted',
            receiptType: itemReceiptType,
            createdBy: makerName,
            makerId: makerIdValue,
            makerSealUrl,
            notes: notes || '',
            paymentDate: paymentDate ? dayjs(paymentDate).format('YYYY-MM-DD') : undefined,
            accountingScenario,
            tradeInAmount: Number(tradeInAmount || 0) || undefined,
            otherIncomeAmount: Number(otherIncomeAmount || 0) || undefined,
            evidenceUrls,
            items: allocatedWithRange,
            chargeRangeStart: explicitChargeRange ? rangeStart : undefined,
            chargeRangeEnd: explicitChargeRange ? rangeEnd : undefined,
            tailCollection: item.tailCollection ? true : undefined
          })
        }
      }

      message.success('提交成功，已生成凭证，需由出纳在收银页点击“确认收款”后才会进入已收入')
      setPreviewVisible(false)
      setRentModalVisible(false)
      setFileList([])
      setPendingValues(null)
      setSelectedVehicles([])
      setTailCollectionRow(null)
      loadData()
    } catch (err) {
      console.error(err)
      message.error('提交失败,请重试')
    } finally {
      setSubmittingReceipt(false)
    }
  }

  const getExpiringVehicles = (limitDays?: number) => {
    const now = dayjs()
    const pendingIds = pendingReceipts.map(p => p.vehicleId || p.plate)
    return vehicles
      .filter(v => isReceivableVehicleStatus(v.status))
      .filter(v => getVehicleDueDate(v))
      .filter(v => isLeaseStarted(v, now))
      .filter(v => !pendingIds.includes(v.id) && !pendingIds.includes(v._id))
      .filter(v => {
        if (limitDays === undefined) return true
        const diff = dayjs(getVehicleDueDate(v)).diff(now, 'day')
        return diff <= limitDays && diff >= 0
      })
      .sort((a, b) => dayjs(getVehicleDueDate(a)).diff(dayjs(getVehicleDueDate(b))))
  }

  const getDueDateStatus = (dueDate?: string) => {
    if (!dueDate) return { color: 'default', text: '未设置' }
    const diff = dayjs(dueDate).diff(dayjs(), 'day')
    if (diff <= 0) return { color: 'error', text: '已逾期' }
    if (diff <= 3) return { color: 'error', text: `${diff} 天` }
    if (diff <= 7) return { color: 'warning', text: `${diff} 天` }
    return { color: 'success', text: dayjs(dueDate).format('YYYY-MM-DD') }
  }

  const formatDueTagText = (text: string) => {
    if (!text) return text
    if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return `下一个收款日期： ${text}`
    if (text.includes('天')) return `剩余 ${text}`
    return text
  }
  const expiringVehicles = getExpiringVehicles(7)
  const expiringVehiclesAll = getExpiringVehicles()
  const expiringTop = expiringVehiclesAll.slice(0, 3)
  const expiringDisplayList = useMemo(() => {
    if (!showAllExpiring) return expiringTop
    const start = (expiringPage - 1) * expiringPageSize
    return expiringVehiclesAll.slice(start, start + expiringPageSize)
  }, [showAllExpiring, expiringTop, expiringVehiclesAll, expiringPage, expiringPageSize])
  const activeExpiringVehicle = useMemo(
    () => expiringVehiclesAll.find(v => normalizeId(v._id || v.id || v.plate) === activeExpiringVehicleId),
    [expiringVehiclesAll, activeExpiringVehicleId]
  )
  const expiringDepositSum = expiringVehicles.reduce((sum, v) => sum + Number(v.depositAmount || 0), 0)
  const expiringReminderSum = expiringVehicles.reduce((sum, v) => sum + Number(v.reminderAmount ?? v.rentAmount ?? 0), 0)

  if (loading) {
    return (
      <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spin size="large" />
      </div>
    )
  }
  return (
    <div style={{ padding: 24, background: '#f7f8fb', minHeight: '100vh' }}>
      <Card style={{ marginBottom: 24 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Space size="large">
              <Avatar size={64} icon={<UserOutlined />} style={{ backgroundColor: '#cfa972' }} />
              <div>
                <Title level={4} style={{ margin: 0 }}>{user?.nickname || user?.nickName || user?.username || '租车管理员'}</Title>
                <Text type="secondary">{user?.info || '租车管理员'}</Text>
              </div>
            </Space>
          </Col>
          <Col>
            <Space>
              <Button onClick={() => {
                customerLedgerForm.setFieldsValue({
                  customer: '',
                  periodType: 'month',
                  endDate: dayjs()
                })
                setCustomerLedgerVisible(true)
              }}>
                导出客户明细账
              </Button>
              <Button onClick={() => {
                dailyReportForm.setFieldsValue({
                  dateRange: [dayjs().startOf('month'), dayjs()]
                })
                setDailyReportVisible(true)
              }}>
                导出日报表
              </Button>
              {canViewBoss && (
                <Button onClick={() => navigate('/boss')}>老板页面</Button>
              )}
              <Button icon={<LogoutOutlined />} onClick={handleLogout}>
              退出登录
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic title="总车辆数" value={statistics?.total || 0} prefix={<CarOutlined />} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card hoverable onClick={() => setExpiringModalVisible(true)}>
            <Statistic title="即将到期车辆" value={statistics?.expiring || 0} valueStyle={{ color: '#d89614' }} prefix={<ClockCircleOutlined />} />
            <div style={{ marginTop: 4, color: '#999' }}>租金合计 ¥{expiringReminderSum.toFixed(2)}</div>
            <div style={{ marginTop: 2, color: '#999' }}>押金合计 ¥{expiringDepositSum.toFixed(2)}</div>
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic title="待收款收据" value={pendingReceipts.length} valueStyle={{ color: '#cfa972' }} prefix={<DollarOutlined />} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic title="已逾期收据" value={statistics?.expired || 0} valueStyle={{ color: '#ff4d4f' }} prefix={<ExclamationCircleOutlined />} />
          </Card>
        </Col>
      </Row>


      <Card
        style={{
          marginBottom: 24,
          borderColor: expiringVehiclesAll.length ? 'var(--warning)' : 'var(--line)',
          background: expiringVehiclesAll.length ? 'rgba(192, 122, 31, 0.06)' : undefined
        }}
      >
        <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
          <Col>
            <Space>
              <ClockCircleOutlined style={{ color: 'var(--warning)' }} />
              <Text strong>到期提醒</Text>
              <Tag color={expiringVehiclesAll.length ? 'orange' : 'default'}>
                {expiringVehiclesAll.length} 台
              </Tag>
              {expiringVehiclesAll.length ? (
                <Text type="secondary">请优先处理即将到期的车辆</Text>
              ) : (
                <Text type="secondary">当前没有需要提醒的车辆</Text>
              )}
            </Space>
          </Col>
          <Col>
            <Button
              type="primary"
              onClick={() => {
                setShowAllExpiring(prev => {
                  const next = !prev
                  if (next) {
                    setExpiringPage(1)
                    setActiveExpiringVehicleId('')
                  }
                  return next
                })
              }}
              disabled={!expiringVehiclesAll.length}
            >
              {showAllExpiring ? '收起' : '查看全部'}
            </Button>
          </Col>
        </Row>
        {expiringVehiclesAll.length > 0 && (
          <>
            <List
              itemLayout="vertical"
              dataSource={expiringDisplayList}
              renderItem={(vehicle) => {
                const dueDate = getVehicleDueDate(vehicle)
                const vehicleId = normalizeId(vehicle._id || vehicle.id || vehicle.plate)
                const isActive = vehicleId === activeExpiringVehicleId
                return (
                  <List.Item
                    key={vehicleId}
                    style={{ cursor: 'pointer' }}
                    onClick={() => setActiveExpiringVehicleId(vehicleId)}
                  >
                    <List.Item.Meta
                      avatar={<Avatar style={{ backgroundColor: '#cfa972' }}>{vehicle.plate}</Avatar>}
                      title={
                        <Space>
                          <Text strong>{vehicle.plate}</Text>
                          <Tag color="orange">下一个收款日期： {dueDate ? dayjs(dueDate).format('YYYY-MM-DD') : '-'}</Tag>
                          <Tag color="gold">租金 ¥{Number(vehicle.reminderAmount ?? vehicle.rentAmount ?? 0).toFixed(2)}</Tag>
                          {isActive && <Tag color="blue">已展开</Tag>}
                        </Space>
                      }
                      description={
                        <div>
                          <Text type="secondary">公司：</Text>
                          <Text>{vehicle.company || '-'}</Text>
                          <Text type="secondary" style={{ marginLeft: 12 }}>联系人：</Text>
                          <Text>{vehicle.contactPerson || '-'}</Text>
                        </div>
                      }
                    />
                  </List.Item>
                )
              }}
            />
            {showAllExpiring && expiringVehiclesAll.length > expiringPageSize && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                <Pagination
                  current={expiringPage}
                  pageSize={expiringPageSize}
                  total={expiringVehiclesAll.length}
                  showSizeChanger
                  pageSizeOptions={['8', '12', '20']}
                  showTotal={(total) => `共 ${total} 台`}
                  onChange={(page, size) => {
                    setExpiringPage(page)
                    if (size && size !== expiringPageSize) {
                      setExpiringPageSize(size)
                      setExpiringPage(1)
                    }
                  }}
                />
              </div>
            )}
          </>
        )}
        {activeExpiringVehicle && (
          <Card size="small" style={{ marginTop: 12, borderColor: 'var(--warning)' }}>
            <Row gutter={16}>
              <Col span={8}><Text type="secondary">车牌</Text> <Text strong>{activeExpiringVehicle.plate || '-'}</Text></Col>
              <Col span={8}><Text type="secondary">下一个收款日期</Text> <Text>{(() => {
                const due = getVehicleDueDate(activeExpiringVehicle)
                return due ? dayjs(due).format('YYYY-MM-DD') : '-'
              })()}</Text></Col>
              <Col span={8}><Text type="secondary">待收</Text> <Text strong style={{ color: '#cf1322' }}>￥{getOutstanding(activeExpiringVehicle).toFixed(2)}</Text></Col>
            </Row>
            <Row gutter={16} style={{ marginTop: 8 }}>
              <Col span={8}><Text type="secondary">公司</Text> <Text>{activeExpiringVehicle.company || '-'}</Text></Col>
              <Col span={8}><Text type="secondary">联系人</Text> <Text>{activeExpiringVehicle.contactPerson || '-'}</Text></Col>
              <Col span={8}><Text type="secondary">电话</Text> <Text>{activeExpiringVehicle.phone || '-'}</Text></Col>
            </Row>
            <Row gutter={16} style={{ marginTop: 8 }}>
              <Col span={8}><Text type="secondary">提醒金额</Text> <Text>￥{Number(activeExpiringVehicle.reminderAmount ?? activeExpiringVehicle.rentAmount ?? 0).toFixed(2)}</Text></Col>
              <Col span={8}><Text type="secondary">提醒周期</Text> <Text>{getPeriodLabel(activeExpiringVehicle.reminderPeriod || activeExpiringVehicle.rentPeriod, activeExpiringVehicle.reminderPeriodMonths)}</Text></Col>
              <Col span={8}><Text type="secondary">收款截止日期</Text> <Text>{(() => {
                const deadline = getReceiptDeadlineForVehicle(activeExpiringVehicle)
                return deadline && dayjs(deadline).isValid()
                  ? dayjs(deadline).format('YYYY-MM-DD')
                  : '-'
              })()}</Text></Col>
            </Row>
            <Row gutter={16} style={{ marginTop: 8 }}>
              <Col span={24}><Text type="secondary">地址</Text> <Text>{activeExpiringVehicle.address || '-'}</Text></Col>
            </Row>
            <Row gutter={16} style={{ marginTop: 8 }}>
              <Col span={24}><Text type="secondary">备注/配置</Text> <Text>{activeExpiringVehicle.configuration || activeExpiringVehicle.notes || '-'}</Text></Col>
            </Row>
            <Space style={{ marginTop: 12 }}>
              <Button type="primary" onClick={() => handleOpenRentForm(activeExpiringVehicle)} disabled={!canManageVehicles}>去收款</Button>
              <Button onClick={() => handleOpenRentForm(activeExpiringVehicle)} disabled={!canManageVehicles}>修改/续租</Button>
              <Button onClick={() => jumpToVehicleDetails(activeExpiringVehicle)}>定位到在租列表</Button>
            </Space>
          </Card>
        )}
      </Card>

      <Row gutter={16} style={{ marginBottom: 24 }} justify="space-between" align="middle">
        <Col>
          <Button type="primary" icon={<EditOutlined />} onClick={() => handleOpenRentForm()} size="large" disabled={!canManageVehicles}>
          新增车辆
          </Button>
        </Col>
        <Col>
          <Space>
            <Button onClick={openLeaseModal} disabled={!canManageVehicles}>
              退租/再出租单
            </Button>
            <Button onClick={() => { setRentAdjustFileList([]); setRentAdjustModalVisible(true) }} disabled={!canManageVehicles}>
              更新租金申请
            </Button>
          </Space>
        </Col>
        <Col>
          <Input.Search
            placeholder="车牌号/车型/公司/联系人/电话..."
            allowClear
            enterButton={<SearchOutlined />}
            size="large"
            style={{ width: '100%', maxWidth: 360 }}
            onSearch={handleSearch}
            onChange={(e) => {
              if (!e.target.value) handleSearch('')
            }}
          />
        </Col>
      </Row>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col>
          <Space>
            <Text type="secondary">片区筛选：</Text>
            {(['all', 'A', 'B', 'C', 'D'] as const).map(area => (
              <Button
                key={area}
                size="small"
                type={areaFilter === area ? 'primary' : 'default'}
                onClick={() => {
                  setAreaFilter(area)
                  setCurrentPage(1)
                }}
              >
                {area === 'all' ? '全部' : `${area}片区`}
              </Button>
            ))}
          </Space>
        </Col>
      </Row>

      <Card ref={rentDetailsRef}>
        <Tabs
          activeKey={activeTab}
          onChange={(key) => setActiveTab(key as any)}
          items={[
            {
              key: 'rent',
              label: `在租/承包车辆 (${filteredRentedVehicles.length})`,
              children: (
                <>
                  <List
                    itemLayout="vertical"
                    dataSource={paginatedRentedVehicles}
                    renderItem={(vehicle) => {
                      const leaseStarted = isLeaseStarted(vehicle, dayjs())
                      const dueDate = getVehicleDueDate(vehicle)
                      const receiptDeadlineDisplay = getReceiptDeadlineForVehicle(vehicle)
                      const dueStatus = leaseStarted
                        ? getDueDateStatus(dueDate)
                        : { color: 'default', text: '未开始' }
                      const dueTagText = formatDueTagText(dueStatus.text)
                      const outstanding = getOutstanding(vehicle)
                      return (
                        <List.Item
                          key={vehicle._id || vehicle.id || vehicle.plate}
                          actions={[
                            <Button key="edit" type="link" icon={<EditOutlined />} onClick={() => handleOpenRentForm(vehicle)} disabled={!canManageVehicles}>
                              修改/续租
                            </Button>,
                            <Button key="collect" type="link" icon={<CheckCircleOutlined />} onClick={() => handleOpenRentForm(vehicle)} disabled={!canManageVehicles}>
                              收款
                            </Button>
                          ]}
                        >
                          <List.Item.Meta
                            avatar={<Avatar style={{ backgroundColor: '#cfa972' }}>{vehicle.plate}</Avatar>}
                            title={
                              <Space>
                                <Text strong>{vehicle.plate}</Text>
                                <Tag color={dueStatus.color}>{dueTagText}</Tag>
                                {vehicle.status === 'contracted' && <Tag color="cyan">承包车</Tag>}
                                {vehicle.area && <Tag color="geekblue">{normalizeArea(vehicle.area)}片区</Tag>}
                                <Tag color={outstanding > 0 ? 'red' : 'green'}>
                                待收 ￥{outstanding.toFixed(2)}
                                </Tag>
                              </Space>
                            }
                            description={
                              <div>
                                <Row gutter={16}>
                                  <Col span={12}><Text type="secondary">车型</Text> <Text>{vehicle.type}</Text></Col>
                                  <Col span={12}><Text type="secondary">公司</Text> <Text>{vehicle.company}</Text></Col>
                                </Row>
                                <Row gutter={16}>
                                  <Col span={12}><Text type="secondary">联系人</Text> <Text>{vehicle.contactPerson || '-'} {(vehicle as any).contactPerson2 ? `/ ${(vehicle as any).contactPerson2}` : ''}</Text></Col>
                                  <Col span={12}><Text type="secondary">电话</Text> <Text>{vehicle.phone} {(vehicle as any).phone2 ? `/ ${(vehicle as any).phone2}` : ''}</Text></Col>
                                </Row>
                                <Row gutter={16}>
                                  <Col span={12}><Text type="secondary">租金</Text> <Text strong style={{ color: '#52c41a' }}>￥{vehicle.rentAmount || 0}</Text></Col>
                                  <Col span={12}><Text type="secondary">地址</Text> <Text>{vehicle.address || '-'}</Text></Col>
                                </Row>
                                <Row gutter={16}>
                                  <Col span={12}><Text type="secondary">提醒金额</Text> <Text>￥{vehicle.reminderAmount ?? vehicle.rentAmount ?? 0}</Text></Col>
                                  <Col span={12}><Text type="secondary">提醒周期</Text> <Text>{getPeriodLabel(vehicle.reminderPeriod || vehicle.rentPeriod, vehicle.reminderPeriodMonths)}</Text></Col>
                                </Row>
                                <Row gutter={16}>
                                  <Col span={12}><Text type="secondary">押金</Text> <Text>{vehicle.depositAmount ?? '-'}</Text></Col>
                                  <Col span={12}><Text type="secondary">收款截止日期</Text> <Text>{receiptDeadlineDisplay && dayjs(receiptDeadlineDisplay).isValid() ? dayjs(receiptDeadlineDisplay).format('YYYY-MM-DD') : '-'}</Text></Col>
                                </Row>
                                <Row gutter={16}>
                                  <Col span={24}><Text type="secondary">备注</Text> <Text>{vehicle.notes || '-'}</Text></Col>
                                </Row>
                              </div>
                            }
                          />
                        </List.Item>
                      )
                    }}
                  />
                  {filteredRentedVehicles.length > 0 && (
                    <div style={{ textAlign: 'center', marginTop: 16 }}>
                      <Pagination
                        current={currentPage}
                        pageSize={pageSize}
                        total={filteredRentedVehicles.length}
                        showSizeChanger
                        showQuickJumper
                        pageSizeOptions={['10', '20', '50']}
                        onChange={(page, size) => {
                          setCurrentPage(page)
                          if (size !== pageSize) {
                            setPageSize(size)
                            setCurrentPage(1)
                          }
                        }}
                        onShowSizeChange={(_, size) => {
                          setPageSize(size)
                          setCurrentPage(1)
                        }}
                        showTotal={(total, range) => `第 ${range[0]}-${range[1]} 条 / 共 ${total} 条`}
                      />
                    </div>
                  )}
                </>
              )
            },
            {
              key: 'returnedDebt',
              label: (
                <Badge count={returnedDebtRows.length} size="small">
                  <span>退租车辆待收</span>
                </Badge>
              ),
              children: (
                <List
                  itemLayout="vertical"
                  dataSource={returnedDebtRows}
                  locale={{ emptyText: '暂无退租待收车辆' }}
                  renderItem={(row: any) => {
                    const vehicle = row.vehicle as Vehicle
                    const historicalLease = row.historicalLease || {}
                    const returnDoc = row.returnDoc || {}
                    const returnDate = row.returnAt?.isValid?.() ? row.returnAt.format('YYYY-MM-DD HH:mm') : '-'
                    return (
                      <List.Item key={`returned-${vehicle._id || vehicle.id || vehicle.plate}`}>
                        <List.Item.Meta
                          avatar={<Avatar style={{ backgroundColor: '#999' }}>{vehicle.plate}</Avatar>}
                          title={(
                            <Space>
                              <Text strong>{vehicle.plate}</Text>
                              <Tag color="default">已退租</Tag>
                              <Tag color="red">定格待收 ￥{Number(row.frozenOutstanding || 0).toFixed(2)}</Tag>
                              <Tag color="gold">退租欠款账期 {row.debtPeriodLabel || '-'}</Tag>
                              {Number(row.tailCollectedAmount || 0) > 0 && <Tag color="green">已收尾款 ￥{Number(row.tailCollectedAmount || 0).toFixed(2)}</Tag>}
                            </Space>
                          )}
                          description={(
                            <div>
                              <Row gutter={16}>
                                <Col span={12}><Text type="secondary">车型</Text> <Text>{vehicle.type || '-'}</Text></Col>
                                <Col span={12}><Text type="secondary">公司</Text> <Text>{historicalLease.company || returnDoc.unitName || vehicle.company || '-'}</Text></Col>
                              </Row>
                              <Row gutter={16}>
                                <Col span={12}><Text type="secondary">联系人</Text> <Text>{historicalLease.contactPerson || vehicle.contactPerson || '-'}</Text></Col>
                                <Col span={12}><Text type="secondary">电话</Text> <Text>{historicalLease.phone || vehicle.phone || '-'}</Text></Col>
                              </Row>
                              <Row gutter={16}>
                                <Col span={12}><Text type="secondary">退租时间</Text> <Text>{returnDate}</Text></Col>
                                <Col span={12}><Text type="secondary">退租单位</Text> <Text>{returnDoc.unitName || '-'}</Text></Col>
                              </Row>
                              <Row gutter={16}>
                                <Col span={24}><Text type="secondary">备注/配置</Text> <Text>{vehicle.configuration || vehicle.notes || '-'}</Text></Col>
                              </Row>
                              <Space style={{ marginTop: 12 }}>
                                <Button type="primary" onClick={() => handleOpenRentForm(vehicle, { tailCollectionRow: row })} disabled={!canManageVehicles}>
                                  收尾款
                                </Button>
                              </Space>
                            </div>
                          )}
                        />
                      </List.Item>
                    )
                  }}
                />
              )
            },
            {
              key: 'pending',
              label: (
                <Badge count={signedReceiptDocs.length + signedLeaseForms.length} size="small">
                  <span>已签单据</span>
                </Badge>
              ),
              children: (
                <>
                  <Input.Search
                    placeholder="搜索已签单据(车牌/公司/制单人/备注/单号)"
                    allowClear
                    value={signedDocSearchKeyword}
                    onChange={(e) => setSignedDocSearchKeyword(e.target.value)}
                    style={{ marginBottom: 12, maxWidth: 420 }}
                  />
                  <List
                    itemLayout="vertical"
                    dataSource={filteredSignedReceiptDocs}
                    locale={{ emptyText: '暂无已签收款收据' }}
                    renderItem={(receipt: any) => {
                      const vehicle = vehicles.find(v => v.id === receipt.vehicleId || v._id === receipt.vehicleId || v.plate === receipt.plate)
                      const plate = receipt.plate || vehicle?.plate || '未知车牌'
                      return (
                        <List.Item
                          key={receipt._id || receipt.id || plate}
                          actions={[
                            <Button key="print" type="primary" onClick={() => handlePrintReceipt(receipt)}>
                              打印收据
                            </Button>,
                            receipt.cashierSignatureUrl ? (
                              <Button key="sig" onClick={() => openCloudFile(receipt.cashierSignatureUrl)}>
                                查看出纳签名
                              </Button>
                            ) : null
                          ].filter((action) => Boolean(action))}
                        >
                          <List.Item.Meta
                            avatar={<Avatar style={{ backgroundColor: '#52c41a' }}>{plate}</Avatar>}
                            title={
                              <Space>
                                <Text strong>{plate}</Text>
                                <Tag color="green">已签收据</Tag>
                              </Space>
                            }
                            description={(
                              <div>
                                <Row gutter={16}>
                                  <Col span={12}><Text type="secondary">公司</Text> <Text>{receipt.company || vehicle?.company || '-'}</Text></Col>
                                  <Col span={12}><Text type="secondary">金额</Text> <Text strong>￥{Number(receipt.amount || 0).toFixed(2)}</Text></Col>
                                </Row>
                                <Row gutter={16}>
                                  <Col span={12}><Text type="secondary">制单人</Text> <Text>{receipt.createdBy || '-'}</Text></Col>
                                  <Col span={12}><Text type="secondary">出纳</Text> <Text>{receipt.cashierName || '-'}</Text></Col>
                                </Row>
                                <Row gutter={16}>
                                  <Col span={12}><Text type="secondary">签名时间</Text> <Text>{receipt.verifiedAt ? dayjs(receipt.verifiedAt).format('YYYY-MM-DD HH:mm') : '-'}</Text></Col>
                                  <Col span={12}><Text type="secondary">状态</Text> <Tag color="green">{receipt.status || '-'}</Tag></Col>
                                </Row>
                              </div>
                            )}
                          />
                        </List.Item>
                      )
                    }}
                  />

                  <Divider orientation="left">已签退租/再出租单（可打印封存）</Divider>
                  <List
                    itemLayout="vertical"
                    dataSource={filteredSignedLeaseForms}
                    locale={{ emptyText: '暂无已签退租/再出租单' }}
                    renderItem={(doc: any) => (
                      <List.Item
                        key={doc._id || doc.id || doc.plate}
                        actions={[
                          <Button key="print" type="primary" onClick={() => handlePrintLeaseForm(doc)}>
                            打印单据
                          </Button>,
                          doc.cashierSignatureUrl ? (
                            <Button key="sig" onClick={() => openCloudFile(doc.cashierSignatureUrl)}>
                              查看出纳签名
                            </Button>
                          ) : null,
                          <Button key="cancel" danger onClick={() => handleCancelLeaseForm(doc)}>
                            撤销
                          </Button>
                        ].filter((action) => Boolean(action))}
                      >
                        <List.Item.Meta
                          avatar={<Avatar style={{ backgroundColor: '#cfa972' }}>{doc.plate || '-'}</Avatar>}
                          title={(
                            <Space>
                              <Text strong>{doc.plate || '-'}</Text>
                              <Tag color={doc.type === 'return' ? 'volcano' : 'blue'}>
                                {doc.type === 'return' ? '退租单' : '出租单'}
                              </Tag>
                              <Tag color="green">已签名</Tag>
                            </Space>
                          )}
                          description={(
                            <div>
                              <Row gutter={16}>
                                <Col span={12}><Text type="secondary">公司</Text> <Text>{doc.company || '-'}</Text></Col>
                                <Col span={12}><Text type="secondary">{doc.type === 'relet' ? '出租单位' : '退租单位'}</Text> <Text>{doc.unitName || '-'}</Text></Col>
                              </Row>
                              <Row gutter={16}>
                                <Col span={12}><Text type="secondary">制单人</Text> <Text>{doc.createdBy || '-'}</Text></Col>
                                <Col span={12}><Text type="secondary">出纳</Text> <Text>{doc.cashierName || '-'}</Text></Col>
                              </Row>
                              <Row gutter={16}>
                                <Col span={12}><Text type="secondary">签名时间</Text> <Text>{doc.signedAt ? dayjs(doc.signedAt).format('YYYY-MM-DD HH:mm') : '-'}</Text></Col>
                                <Col span={12}><Text type="secondary">备注</Text> <Text>{doc.notes || '-'}</Text></Col>
                              </Row>
                            </div>
                          )}
                        />
                      </List.Item>
                    )}
                  />
                </>
              )
            },
            {
              key: 'rentAdjust',
              label: (
                <Badge count={rentAdjustRequests.length} size="small">
                  <span>租金申请记录</span>
                </Badge>
              ),
              children: (
                <List
                  itemLayout="vertical"
                  dataSource={[...rentAdjustRequests].sort((a: any, b: any) => dayjs(b.createdAt).valueOf() - dayjs(a.createdAt).valueOf())}
                  locale={{ emptyText: '暂无记录' }}
                  renderItem={(item: any) => (
                    <List.Item>
                      <List.Item.Meta
                        avatar={<Avatar style={{ backgroundColor: '#cfa972' }}>{item.plate || '-'}</Avatar>}
                        title={(
                          <Space>
                            <Text strong>{item.plate || '-'}</Text>
                            <Tag color={item.status === 'approved' ? 'green' : item.status === 'filed' ? 'blue' : 'orange'}>
                              {item.status === 'approved' ? '已处理' : item.status === 'filed' ? '已归档' : '待处理'}
                            </Tag>
                          </Space>
                        )}
                        description={(
                          <div>
                            <Row gutter={16}>
                              <Col span={12}><Text type="secondary">公司</Text> <Text>{item.company || '-'}</Text></Col>
                              <Col span={12}><Text type="secondary">当前租金</Text> <Text>￥{Number(item.currentRent || 0).toFixed(2)}</Text></Col>
                            </Row>
                            <Row gutter={16}>
                              <Col span={12}><Text type="secondary">制单人</Text> <Text>{item.createdBy || '-'}</Text></Col>
                              <Col span={12}><Text type="secondary">提交时间</Text> <Text>{item.createdAt ? dayjs(item.createdAt).format('YYYY-MM-DD HH:mm') : '-'}</Text></Col>
                            </Row>
                            <Row gutter={16}>
                              <Col span={24}><Text type="secondary">说明</Text> <Text>{item.notes || '-'}</Text></Col>
                            </Row>
                            <Row gutter={16}>
                              <Col span={24}>
                                <Text type="secondary">附件</Text>{' '}
                                {Array.isArray(item.attachmentUrls) && item.attachmentUrls.length ? (
                                  <Space wrap>
                                    {item.attachmentUrls.map((url: string, idx: number) => (
                                      <Button key={`${url}-${idx}`} size="small" onClick={() => openCloudFile(url)}>
                                        查看附件{idx + 1}
                                      </Button>
                                    ))}
                                  </Space>
                                ) : (
                                  <Text>-</Text>
                                )}
                              </Col>
                            </Row>
                          </div>
                        )}
                      />
                    </List.Item>
                  )}
                />
              )
            },
          ]}
        />
      </Card>

      <ExpiringVehiclesModal
        visible={expiringModalVisible}
        vehicles={expiringVehiclesAll}
        onClose={() => setExpiringModalVisible(false)}
        getDueDateStatus={getDueDateStatus}
        onEdit={(vehicle) => handleOpenRentForm(vehicle)}
        canEdit={canManageVehicles}
      />

      <RentModal
        visible={rentModalVisible}
        onOk={handlePreviewRentForm}
        onClose={() => setRentModalVisible(false)}
        form={form}
        mergeByCompany={mergeByCompany}
        onMergeByCompanyChange={setMergeByCompany}
        onVehicleSelectionChange={handleVehicleSelectionChange}
        selectedVehicles={selectedVehicles}
        vehicles={vehicles}
        makers={makers}
        getOutstanding={getOutstanding}
        fileList={fileList}
        onFileListChange={setFileList}
      />

      <Modal
        title="退租/再出租单"
        open={leaseModalVisible}
        onOk={handleSubmitLeaseForm}
        onCancel={() => {
          setLeaseModalVisible(false)
          leaseForm.resetFields()
          const match = makers.find(m =>
            [m._id, m.id, m.username, m.nickName].some(val => String(val || '') === String(user?.id || user?.nickName || ''))
          )
          const defaultMakerId = match ? String(match._id || match.id || match.username || match.nickName) : undefined
          leaseForm.setFieldsValue({ type: 'return', makerId: defaultMakerId, returnTime: dayjs() })
        }}
        width={720}
      >
        <div style={{ fontFamily: '"SimSun","STSong","Noto Serif SC",serif', color: '#222' }}>
          <Form form={leaseForm} layout="vertical">
            <div style={{ textAlign: 'center', fontSize: 26, fontWeight: 700, letterSpacing: 6 }}>
              {leaseType === 'relet' ? '出租单' : '退租单'}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12 }}>
              <Form.Item
                name="type"
                rules={[{ required: true, message: '请选择类型' }]}
                style={{ marginBottom: 0, width: '45%' }}
              >
                <Select placeholder="选择类型">
                  <Option value="return">退租单</Option>
                  <Option value="relet">出租单</Option>
                </Select>
              </Form.Item>
              <Form.Item
                name="vehicleId"
                rules={[{ required: true, message: '请选择车辆' }]}
                style={{ marginBottom: 0, width: '45%' }}
              >
                <Select
                  placeholder="选择车辆"
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    String(option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {vehicles.map(v => {
                    const id = String(v._id || v.id || v.plate)
                    const disabled = leaseType === 'relet'
                      ? false
                      : Boolean(v.returnSigned && !isReceivableVehicleStatus(v.status))
                    return (
                      <Option key={id} value={id} disabled={disabled}>
                        {v.plate} - {v.company}
                      </Option>
                    )
                  })}
                </Select>
              </Form.Item>
            </div>
            {leaseType !== 'relet' && (
              <Form.Item
                label="退租时间"
                name="returnTime"
                rules={[{ required: true, message: '请选择退租时间' }]}
                style={{ marginTop: 12, marginBottom: 0 }}
              >
                <DatePicker showTime style={{ width: '100%' }} placeholder="请选择退租时间" />
              </Form.Item>
            )}
            <Form.Item shouldUpdate={(prev, cur) => prev.type !== cur.type} noStyle>
              {() => {
                const label = leaseType === 'relet' ? '出租单位' : '退租单位'
                const isReturnType = !leaseType || leaseType === 'return'
                return (
                  <div style={{ marginTop: 8 }}>
                    <div>{label}：</div>
                    <Form.Item
                      name="unitName"
                      rules={[{ required: true, message: `请输入${label}` }]}
                      style={{ marginBottom: 0 }}
                    >
                      <Input
                        placeholder={`请输入${label}`}
                        disabled={isReturnType}
                      />
                    </Form.Item>
                  </div>
                )
              }}
            </Form.Item>
            {leaseType === 'relet' && (
              <>
                <Row gutter={12} style={{ marginTop: 12 }}>
                  <Col span={12}>
                    <Form.Item label="起租日" name="startDate" rules={[{ required: true, message: '请选择起租日' }]}>
                      <DatePicker style={{ width: '100%' }} placeholder="请选择起租日" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="到期日" name="dueDate" rules={[{ required: true, message: '请选择到期日' }]}>
                      <DatePicker style={{ width: '100%' }} placeholder="请选择到期日" />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={12}>
                  <Col span={12}>
                    <Form.Item label="租金" name="rentAmount" rules={[{ required: true, message: '请输入租金' }]}>
                      <InputNumber style={{ width: '100%' }} min={0} placeholder="请输入租金" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="付款周期" required>
                      <Form.Item name="rentPeriod" noStyle initialValue="yearly">
                        <Select style={{ width: '100%' }}>
                          {rentPeriodOptions.map(opt => (
                            <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={12}>
                  <Col span={12}>
                    {leaseRentPeriod === 'custom' ? (
                      <Form.Item
                        label="付款月数"
                        name="rentPeriodMonths"
                        rules={[{
                          validator: (_, v) => {
                            if (!v || Number(v) <= 0) return Promise.reject(new Error('请输入大于0的月数'))
                            return Promise.resolve()
                          }
                        }]}
                      >
                        <InputNumber style={{ width: '100%' }} min={1} placeholder="请输入月数" />
                      </Form.Item>
                    ) : (
                      <Form.Item label="付款月数">
                        <InputNumber style={{ width: '100%' }} disabled placeholder="仅自定义付款周期可填" />
                      </Form.Item>
                    )}
                  </Col>
                  <Col span={12}>
                    <Form.Item name="receivableMode" label="代收算法" initialValue="daily_365" rules={[{ required: true, message: '请选择代收算法' }]}>
                      <Select placeholder="请选择代收算法">
                        {receivableModeOptions.map(opt => (
                          <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={12}>
                  <Col span={12}>
                    {leaseReceivableMode === 'monthly_plan' ? (
                      <Form.Item
                        label="收款月数"
                        name="receivableMonths"
                        rules={[{
                          validator: (_, v) => {
                            const n = Number(v || 0)
                            if (Number.isFinite(n) && n > 0) return Promise.resolve()
                            return Promise.reject(new Error('请输入大于0的月数'))
                          }
                        }]}
                      >
                        <InputNumber style={{ width: '100%' }} min={1} max={24} placeholder="例如 10 或 11" />
                      </Form.Item>
                    ) : (
                      <Form.Item label="收款月数">
                        <InputNumber style={{ width: '100%' }} disabled placeholder="仅按年金/收款月数模式可填" />
                      </Form.Item>
                    )}
                  </Col>
                  <Col span={12}>
                    <Form.Item label="提醒周期" required>
                      <Input.Group compact>
                        <Form.Item name="reminderPeriod" noStyle rules={[{ required: true, message: '请选择提醒周期' }]} initialValue="monthly">
                          <Select
                            style={{ width: leaseForm.getFieldValue('reminderPeriod') === 'custom' ? '60%' : '100%' }}
                            placeholder="选择提醒周期"
                            onChange={(val) => {
                              if (val !== 'custom') leaseForm.setFieldValue('reminderPeriodMonths', undefined)
                            }}
                          >
                            {reminderPeriodOptions.map(opt => (
                              <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                            ))}
                          </Select>
                        </Form.Item>
                        {leaseForm.getFieldValue('reminderPeriod') === 'custom' && (
                          <Form.Item
                            name="reminderPeriodMonths"
                            noStyle
                            rules={[{
                              validator: (_, v) => {
                                if (!v || Number(v) <= 0) return Promise.reject(new Error('请输入大于0的月数'))
                                return Promise.resolve()
                              }
                            }]}
                          >
                            <InputNumber style={{ width: '40%' }} min={1} placeholder="月数" />
                          </Form.Item>
                        )}
                      </Input.Group>
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={12}>
                  <Col span={12}>
                    <Form.Item label="提醒金额" name="reminderAmount" rules={[{ required: true, message: '请输入提醒金额' }]}>
                      <InputNumber style={{ width: '100%' }} min={0} placeholder="请输入提醒金额" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="地址" name="address">
                      <Input placeholder="请输入地址" />
                    </Form.Item>
                  </Col>
                </Row>
              </>
            )}

            <div style={{ border: '1px solid #333', marginTop: 12 }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: '80px 1fr 150px 60px 60px 90px 110px',
                fontWeight: 700,
                borderBottom: '1px solid #333'
              }}>
                <div style={{ padding: '6px 8px', borderRight: '1px solid #333' }}>货号</div>
                <div style={{ padding: '6px 8px', borderRight: '1px solid #333' }}>品名及规格</div>
                <div style={{ padding: '6px 8px', borderRight: '1px solid #333' }}>
                  {leaseType === 'relet' ? '出租单位' : '退租单位'}
                </div>
                <div style={{ padding: '6px 8px', borderRight: '1px solid #333' }}>单位</div>
                <div style={{ padding: '6px 8px', borderRight: '1px solid #333' }}>数量</div>
                <div style={{ padding: '6px 8px', borderRight: '1px solid #333' }}>单价</div>
                <div style={{ padding: '6px 8px' }}>金额</div>
              </div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: '80px 1fr 150px 60px 60px 90px 110px'
              }}>
                <div style={{ padding: '6px 8px', borderRight: '1px solid #333' }} />
                <div style={{ padding: '6px 8px', borderRight: '1px solid #333' }}>
                  {(() => {
                    const vehicle = vehicles.find(v => String(v._id || v.id || v.plate) === String(leaseVehicleId))
                    const plate = vehicle?.plate || '-'
                    const model = vehicle?.type ? ` - ${vehicle.type}` : ''
                    return `车辆租赁（${plate}）${model}`
                  })()}
                </div>
                <div style={{ padding: '6px 8px', borderRight: '1px solid #333' }}>
                  {leaseUnitName || '-'}
                </div>
                <div style={{ padding: '6px 8px', borderRight: '1px solid #333' }}>台</div>
                <div style={{ padding: '6px 8px', borderRight: '1px solid #333' }}>1</div>
                <div style={{ padding: '6px 8px', borderRight: '1px solid #333', textAlign: 'right' }}>-</div>
                <div style={{ padding: '6px 8px', textAlign: 'right' }}>-</div>
              </div>
            </div>
            <div style={{ marginTop: 8 }}>
              <div>配置：</div>
              <div>
                {(() => {
                  const vehicle = vehicles.find(v => String(v._id || v.id || v.plate) === String(leaseVehicleId))
                  return vehicle?.configuration || vehicle?.notes || '-'
                })()}
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 8 }}>
              <div>
                <div style={{ marginBottom: 4 }}>制单人：</div>
                <Form.Item name="makerId" style={{ marginBottom: 8, width: 220 }} rules={[{ required: true, message: '请选择制单人' }]}>
                  <Select placeholder="选择制单人">
                    {makers.map((m: any) => {
                      const id = String(m._id || m.id || m.username || m.nickName)
                      return (
                        <Option key={id} value={id}>
                          {m.nickName || m.username || id}
                        </Option>
                      )
                    })}
                  </Select>
                </Form.Item>
                {leaseMakerSealPreviewUrl ? (
                  <img
                    src={leaseMakerSealPreviewUrl}
                    alt="maker seal"
                    style={{ maxWidth: 220, maxHeight: 120, border: '1px solid #ddd', borderRadius: 4 }}
                  />
                ) : (
                  <div style={{ color: '#999' }}>-</div>
                )}
              </div>
              <div>
                <div style={{ marginBottom: 4 }}>出纳：</div>
                <div style={{ minHeight: 32, display: 'flex', alignItems: 'center' }}>-</div>
                <div style={{ marginTop: 8, color: '#999' }}>-</div>
              </div>
            </div>
            <div style={{ marginTop: 8 }}>
              <div>备注：</div>
              <Form.Item name="notes" style={{ marginBottom: 0 }}>
                <Input.TextArea rows={2} placeholder="备注(可选)" />
              </Form.Item>
            </div>
          </Form>
        </div>
      </Modal>

      <Modal
        title="更新租金申请"
        open={rentAdjustModalVisible}
        onOk={handleSubmitRentAdjustRequest}
        onCancel={() => {
          setRentAdjustModalVisible(false)
          rentAdjustForm.resetFields()
          setRentAdjustFileList([])
        }}
        width={720}
      >
        <div style={{ fontFamily: '"SimSun","STSong","Noto Serif SC",serif', color: '#222' }}>
          <Form form={rentAdjustForm} layout="vertical">
            <div style={{ textAlign: 'center', fontSize: 26, fontWeight: 700, letterSpacing: 6 }}>
              更新租金申请单
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12 }}>
              <div>申请人：{resolveMaker(rentAdjustMakerId)?.nickName || user?.nickName || '-'}</div>
              <div>日期：{dayjs().format('YYYY-MM-DD')}</div>
            </div>
            <Form.Item name="vehicleId" label="选择车辆" rules={[{ required: true, message: '请选择车辆' }]}>
              <Select
                placeholder="选择车辆"
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  String(option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                }
              >
                {vehicles.map(v => {
                  const id = String(v._id || v.id || v.plate)
                  return (
                    <Option key={id} value={id}>
                      {v.plate} - {v.company}
                    </Option>
                  )
                })}
              </Select>
            </Form.Item>
            <div style={{ border: '1px solid #333', marginTop: 12 }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: '80px 1fr 120px 120px',
                fontWeight: 700,
                borderBottom: '1px solid #333'
              }}>
                <div style={{ padding: '6px 8px', borderRight: '1px solid #333' }}>货号</div>
                <div style={{ padding: '6px 8px', borderRight: '1px solid #333' }}>品名及规格</div>
                <div style={{ padding: '6px 8px', borderRight: '1px solid #333' }}>当前租金</div>
                <div style={{ padding: '6px 8px' }}>调整后租金</div>
              </div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: '80px 1fr 120px 120px'
              }}>
                <div style={{ padding: '6px 8px', borderRight: '1px solid #333' }} />
                <div style={{ padding: '6px 8px', borderRight: '1px solid #333' }}>
                  {(() => {
                    const vehicle = vehicles.find(v => String(v._id || v.id || v.plate) === String(rentAdjustVehicleId))
                    const plate = vehicle?.plate || '-'
                    const spec = [vehicle?.type, vehicle?.configuration || vehicle?.notes].filter(Boolean).join(' / ')
                    return `车辆租赁（${plate}）${spec ? ` - ${spec}` : ''}`
                  })()}
                </div>
                <div style={{ padding: '6px 8px', borderRight: '1px solid #333' }}>
                  ￥{(() => {
                    const vehicle = vehicles.find(v => String(v._id || v.id || v.plate) === String(rentAdjustVehicleId))
                    return Number(vehicle?.rentAmount || 0).toFixed(2)
                  })()}
                </div>
                <div style={{ padding: '6px 8px' }}>
                  （人工线下调整）
                </div>
              </div>
            </div>
            <div style={{ marginTop: 8 }}>
              <div>申请说明：</div>
              <Form.Item name="notes" style={{ marginBottom: 0 }}>
                <Input.TextArea rows={2} placeholder="说明(可选)" />
              </Form.Item>
            </div>
            <div style={{ marginTop: 8 }}>
              <div>上传租金申请单：</div>
              <Upload
                fileList={rentAdjustFileList}
                beforeUpload={() => false}
                onChange={({ fileList }) => setRentAdjustFileList(fileList)}
              >
                <Button>上传图片/PDF</Button>
              </Upload>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
              <div>制单人：</div>
              <Form.Item name="makerId" style={{ marginBottom: 0, width: 220 }} rules={[{ required: true, message: '请选择制单人' }]}>
                <Select placeholder="选择制单人">
                  {makers.map((m: any) => {
                    const id = String(m._id || m.id || m.username || m.nickName)
                    return (
                      <Option key={id} value={id}>
                        {m.nickName || m.username || id}
                      </Option>
                    )
                  })}
                </Select>
              </Form.Item>
              <div>处理方式：</div>
              <div>上传归档</div>
            </div>
            {rentAdjustMakerSealPreviewUrl && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                <img
                  src={rentAdjustMakerSealPreviewUrl}
                  alt="maker seal"
                  style={{ maxWidth: 220, maxHeight: 120, border: '1px solid #ddd', borderRadius: 4 }}
                />
              </div>
            )}
          </Form>
        </div>
      </Modal>

      <PreviewModal
        visible={previewVisible}
        onClose={() => setPreviewVisible(false)}
        onConfirm={handleConfirmSubmit}
        confirmLoading={submittingReceipt}
        previewItems={previewItems}
        pendingValues={pendingValues}
        fileList={fileList}
      />

      <Modal
        title="导出客户明细账"
        open={customerLedgerVisible}
        onCancel={() => setCustomerLedgerVisible(false)}
        onOk={handleExportCustomerLedger}
        okText="导出"
      >
        <Form form={customerLedgerForm} layout="vertical">
          <Form.Item name="customer" label="客户">
            <Select
              allowClear
              placeholder="全部客户"
              showSearch
              options={Array.from(new Set(
                vehicles
                  .map(v => String(v.company || '').trim())
                  .filter(Boolean)
              )).sort().map(v => ({ label: v, value: v }))}
            />
          </Form.Item>
          <Form.Item name="periodType" label="时间范围" rules={[{ required: true, message: '请选择时间范围' }]}>
            <Select
              options={[
                { label: '近1个月', value: 'month' },
                { label: '近1年', value: 'year' }
              ]}
            />
          </Form.Item>
          <Form.Item name="endDate" label="截止日期" rules={[{ required: true, message: '请选择截止日期' }]}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="导出日报表"
        open={dailyReportVisible}
        onCancel={() => setDailyReportVisible(false)}
        onOk={handleExportDailyReport}
        okText="导出"
      >
        <Form form={dailyReportForm} layout="vertical">
          <Form.Item name="dateRange" label="开始至截止日期" rules={[{ required: true, message: '请选择日期范围' }]}>
            <DatePicker.RangePicker style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default VehicleManagerPage
